// ESTAÇÕES — página-mãe do monitoramento hidrométrico (E3, 2026-06-18).
// Mapa (maplibre) com todas as estações + lista lateral. Cadastro/edição e
// atribuição a cliente/contrato. Clicar numa estação abre o modal de detalhe
// com gráfico (recharts), janela de análise ajustável, download CSV, QR e
// registro de leitura manual. Backend: comercial-engine op_estacao_* (E2).
// Ver docs/estacoes.md.
import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  App as AntApp, Button, Card, Grid, Modal, Form, Input, Select, InputNumber, Table, Tag,
  Space, Segmented, DatePicker, Spin, Empty, Popconfirm, Typography, Statistic, Row, Col, Tooltip, Tabs,
  Descriptions, Alert,
} from 'antd';
import {
  PlusOutlined, DownloadOutlined, ReloadOutlined, DeleteOutlined, EditOutlined,
  EnvironmentOutlined, QrcodeOutlined, UploadOutlined, EyeOutlined, FilePdfOutlined,
  AppstoreOutlined, BarsOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { usePerms } from '../hooks/usePerms';
import dayjs, { Dayjs } from 'dayjs';
import {
  Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
  Scatter, ComposedChart,
} from 'recharts';
// Escala/poda/unidade dos gráficos hidrológicos — regras vindas do portal do
// cliente (docs/graficos-hidrologicos.md). Módulo puro, compartilhado.
import {
  dominioY, podarBuracos, limiarBuracoMs,
  grandezaDe, ehBarras, fmtValor, janelaAncorada, ancoraDefasada,
} from '../utils/escalaGrafico';
import { QRCodeCanvas } from 'qrcode.react';
import ImportarPlanilhaLeituras from './operacional-estacoes/ImportarPlanilhaLeituras';
// Base do módulo extraída em 2026-08-06 (o arquivo passou de 1.900 linhas e a
// ficha ANA da Fase 4 ainda soma um formulário de ~60 campos).
import {
  Estacao, Leitura, ClienteOpt, ContratoOpt,
  motivoModem, PRESETS, TELE_RES, TELE_AGG, aggDefault, fmtCadencia,
  api, listarClientes, nf, estiloOsm, ajustarPotencia,
  parseLidoEmCelula, parseValorCelula,
} from './operacional-estacoes/shared';
import { ModalMolinetes } from './operacional-estacoes/ModalMolinetes';
import { TabSerie } from './operacional-estacoes/TabSerie';
import { TabConfigTelemetria } from './operacional-estacoes/TabConfigTelemetria';
import { TabFicha } from './operacional-estacoes/TabFicha';
import { ModalQrPlaca } from './operacional-estacoes/ModalQrPlaca';
import { CardsEstacoes, agrupar } from './operacional-estacoes/CardsEstacoes';
import {
  ESTADO_COR, ESTADO_ROTULO, estadoDe, idadeTexto, kpisDe, pendenciasDe, textoCadencia,
} from './operacional-estacoes/situacao';

const { Text, Title } = Typography;

// Largura REAL de um elemento. Existe porque `responsive` do antd decide por
// largura de JANELA, e aqui a tabela divide a linha com o mapa: numa janela de
// 1611 px o container da tabela tem 1176. Decidir coluna pela janela mostrava
// 1464 px de coluna em 1176 px de espaço — rolagem horizontal eterna.
function useLarguraDe(ref: React.RefObject<HTMLDivElement | null>, deps: any[] = []) {
  const [largura, setLargura] = useState<number>(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => setLargura(Math.round(el.getBoundingClientRect().width));
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);
  return largura;
}

// Quanto de tela sobra ABAIXO de um elemento, medido de verdade.
//
// A alternativa seria `calc(100vh - Npx)`, e foi o que eu fiz primeiro: sobrava
// um palmo de branco embaixo. O N depende de tudo que está acima (título, faixa
// de KPI — que quebra em 2 linhas no celular —, barra de filtro que também
// quebra), e isso muda com a largura da tela: nenhuma constante acerta os três
// casos. Perguntar ao próprio elemento onde ele começa acerta sempre.
function useAlturaRestante(ref: React.RefObject<HTMLDivElement | null>, folga = 12, deps: any[] = []) {
  const [altura, setAltura] = useState<number>(420);
  useEffect(() => {
    const medir = () => {
      const el = ref.current;
      if (!el) return;
      const topo = el.getBoundingClientRect().top;
      // Piso de 240: em tela muito baixa (ou com o teclado aberto no celular)
      // é melhor rolar a página do que espremer a lista até não caber uma linha.
      setAltura(Math.max(240, Math.round(window.innerHeight - topo - folga)));
    };
    medir();
    // Duas medidas: uma agora e outra no próximo quadro. A primeira pega o
    // layout antes de a fonte/ícone assentar; sem a segunda, a altura fica
    // alguns pixels errada logo na abertura.
    const t = window.requestAnimationFrame(medir);
    window.addEventListener('resize', medir);
    return () => { window.cancelAnimationFrame(t); window.removeEventListener('resize', medir); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, folga, ...deps]);
  return altura;
}

// Ajuste da curva-chave por potência Q = a·(h − h0)^n (log-log + busca do h0 que
// maximiza o R² no espaço log; R² final reportado no espaço linear de Q).
export default function EstacoesMapa() {
  return <AntApp><EstacoesMapaInner /></AntApp>;
}

function EstacoesMapaInner() {
  const { message } = AntApp.useApp();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const detalheRef = useRef<(id: string) => void>(() => {});
  const [estacoes, setEstacoes] = useState<Estacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState<ClienteOpt[]>([]);
  const [contratos, setContratos] = useState<ContratoOpt[]>([]);
  const [cadastro, setCadastro] = useState<{ open: boolean; editando: Estacao | null }>({ open: false, editando: null });
  const [detalheId, setDetalheId] = useState<string | null>(null);
  const [molinetesOpen, setMolinetesOpen] = useState(false);
  const [vista, setVista] = useState<'cards' | 'lista'>('cards');
  const [agruparPor, setAgruparPor] = useState<'cliente' | 'contrato' | 'nenhum'>('cliente');
  const [busca, setBusca] = useState('');
  const [fCliente, setFCliente] = useState<number[]>([]);
  // ⚠️ Começa em 'todas', NÃO em 'ativas'. O default óbvio esconderia as 4
  // estações de MAIOR acervo histórico (Certel e Profill), que estão inativas —
  // e esconder por default é pior que mostrar demais, porque ninguém procura o
  // que não sabe que existe.
  const [fStatus, setFStatus] = useState<'todas' | 'ativa' | 'inativa'>('todas');
  const [fKpi, setFKpi] = useState<string | null>(null);
  // Congelado por render: `estadoDe`/`idadeTexto` dependem do relógio, e usar
  // Date.now() solto faria o card, a tabela e o KPI discordarem entre si.
  const [agoraMs, setAgoraMs] = useState(() => Date.now());
  const areaRef = useRef<HTMLDivElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);
  // A ordenação da tabela passa a ser controlada por nós — ver o comentário em
  // `linhasLista`. `campo` é a `key` da coluna; `dir`, o que o antd devolve.
  const [ordem, setOrdem] = useState<{ campo: string | null; dir: 'ascend' | 'descend' | null }>(
    { campo: null, dir: null });

  async function carregar() {
    setLoading(true);
    try {
      const d = await api('op_estacao_listar');
      setEstacoes(Array.isArray(d) ? d : []);
      setAgoraMs(Date.now());
    } catch (e: any) { message.error('Erro ao carregar estações: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }
  useEffect(() => {
    carregar();
    listarClientes().then(setClientes).catch(() => {});
    api('op_listar_contratos').then((d: any) => setContratos(Array.isArray(d) ? d : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // detalheRef sempre aponta pro setter atual (usado dentro do handler do mapa)
  detalheRef.current = (id: string) => setDetalheId(id);

  // Filtro em cascata. O do KPI entra por último: clicar em "Sem transmitir"
  // reduz a lista ao problema sem apagar o filtro de cliente que já estava.
  const kpis = useMemo(() => kpisDe(estacoes, agoraMs), [estacoes, agoraMs]);
  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    let v = estacoes;
    if (fStatus !== 'todas') v = v.filter(e => (e.status === 'inativa' ? 'inativa' : 'ativa') === fStatus);
    if (fCliente.length) v = v.filter(e => e.cliente_id != null && fCliente.includes(e.cliente_id));
    if (termo) {
      v = v.filter(e => [e.codigo, e.nome, e.corpo_hidrico, e.cliente_nome, e.contrato_numero]
        .some(x => (x || '').toLowerCase().includes(termo)));
    }
    const k = kpis.find(x => x.chave === fKpi);
    if (k) v = v.filter(k.filtro);
    return v;
  }, [estacoes, busca, fCliente, fStatus, fKpi, kpis]);

  const clientesComEstacao = useMemo(() => {
    const m = new Map<number, string>();
    for (const e of estacoes) if (e.cliente_id != null) m.set(e.cliente_id, e.cliente_nome || `Cliente ${e.cliente_id}`);
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'));
  }, [estacoes]);

  const chaveVisiveis = visiveis.map(e => e.id).join(',');
  // `larga` = a partir de lg, quando o mapa fica AO LADO da lista. Abaixo disso
  // eles empilham e nada tem altura travada.
  const bp = Grid.useBreakpoint();
  const larga = !!bp.lg;
  const filtroAtivo = fStatus !== 'todas' || fCliente.length > 0 || !!busca.trim() || !!fKpi;
  // Remede quando muda algo que empurra a lista pra baixo: a faixa de KPI quebra
  // em 2 linhas no celular e a barra de filtro ganha o "limpar filtros".
  const alturaArea = useAlturaRestante(areaRef, 12, [vista, larga, kpis.length, filtroAtivo]);
  const larguraLista = useLarguraDe(listaRef, [vista, larga]);

  // ── Mapa ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({ container: containerRef.current, style: estiloOsm(), center: [-51.93, -30.03], zoom: 6 });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    mapRef.current = map;
    map.on('load', () => {
      map.addSource('estacoes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } as any });
      // Cor do pino = SITUAÇÃO (a mesma do card e do KPI), não só status. Um
      // mapa onde tudo é verde menos o que está desligado não conta nada: as
      // 4 estações que nunca mediram apareciam verdes.
      map.addLayer({ id: 'estacoes-pt', source: 'estacoes', type: 'circle',
        paint: { 'circle-radius': 8, 'circle-color': ['get', 'cor'],
                 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 } });
      map.addLayer({ id: 'estacoes-lbl', source: 'estacoes', type: 'symbol',
        layout: { 'text-field': ['get', 'codigo'], 'text-size': 11, 'text-offset': [0, 1.4], 'text-anchor': 'top' },
        paint: { 'text-color': '#222', 'text-halo-color': '#fff', 'text-halo-width': 1.5 } });
      map.on('click', 'estacoes-pt', (e) => { const id = e.features?.[0]?.properties?.id; if (id) detalheRef.current(String(id)); });
      map.on('mouseenter', 'estacoes-pt', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'estacoes-pt', () => { map.getCanvas().style.cursor = ''; });
    });
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Atualiza os pontos quando a lista muda
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const src = map.getSource('estacoes') as any;
      if (!src) return;
      const comGeo = visiveis.filter(e => e.latitude != null && e.longitude != null);
      src.setData({ type: 'FeatureCollection', features: comGeo.map(e => ({
        type: 'Feature', geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] },
        properties: { id: e.id, codigo: e.codigo, status: e.status, cor: ESTADO_COR[estadoDe(e, agoraMs)] },
      })) });
      if (comGeo.length) {
        let w = Infinity, s = Infinity, ee = -Infinity, n = -Infinity;
        for (const e of comGeo) { w = Math.min(w, e.longitude!); s = Math.min(s, e.latitude!); ee = Math.max(ee, e.longitude!); n = Math.max(n, e.latitude!); }
        try { map.fitBounds([[w, s], [ee, n]], { padding: 70, maxZoom: 13, duration: 500 }); } catch {}
      }
    };
    if (map.isStyleLoaded() && map.getSource('estacoes')) apply();
    else map.once('idle', apply);
    // Depende da LISTA DE IDS, não do array: `visiveis` é recriado a cada
    // render e o mapa dava um pulo de reenquadramento a cada tecla digitada
    // na busca. Com a chave estável, só reenquadra quando o conjunto muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveVisiveis, agoraMs]);

  async function excluir(e: Estacao) {
    try { await api('op_estacao_excluir', { id: e.id }); message.success('Estação excluída.'); carregar(); }
    catch (err: any) { message.error('Erro: ' + (err.response?.data?.error || err.message)); }
  }

  // `sorter` em TODA coluna que dá pra ordenar (pedido do Lucas): texto por
  // localeCompare pt-BR (senão "Água" cai depois de "Zona"), número por
  // subtração, data pelo instante. Sem `defaultSortOrder` de propósito — a
  // ordem inicial é a do backend (ativas primeiro, depois nome), e ordenar por
  // gravidade faria a lista se remontar sozinha a cada recarga.
  // Ordem em que as colunas ganham espaço. As quatro primeiras nunca saem:
  // sem elas a linha deixa de responder "qual estação, como está, desde quando,
  // e o que eu faço com ela".
  const PRIORIDADE = ['codigo', 'situacao', 'ultimo', 'acoes',
                      'cliente', 'registros', 'contrato', 'tipo', 'pend', 'corpo', 'modem'];
  const txt = (a?: string | null, b?: string | null) => (a || '').localeCompare(b || '', 'pt-BR');
  const dt = (a?: string | null, b?: string | null) => (a ? +new Date(a) : 0) - (b ? +new Date(b) : 0);

  const colunas = [
    // ⚠️ LARGURA OBRIGATÓRIA. Sem ela, esta era a única coluna flexível e o antd
    // a espremia até o cabeçalho virar uma letra por linha.
    { title: 'Estação', dataIndex: 'codigo', key: 'codigo', width: 210, fixed: 'left' as const,
      sorter: (a: Estacao, b: Estacao) => txt(a.codigo, b.codigo),
      render: (v: string, e: Estacao) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ width: 3, height: 22, borderRadius: 2, background: ESTADO_COR[estadoDe(e, agoraMs)], flex: '0 0 3px' }} />
          <div style={{ minWidth: 0 }}>
            <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => setDetalheId(e.id)}>{v}</Button>
            <div style={{ fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.nome}>{e.nome}</div>
          </div>
        </div>
      ) },
    { title: 'Situação', key: 'situacao', width: 118, sorter: (a: Estacao, b: Estacao) => txt(ESTADO_ROTULO[estadoDe(a, agoraMs)], ESTADO_ROTULO[estadoDe(b, agoraMs)]),
      render: (_: any, e: Estacao) => {
        const st = estadoDe(e, agoraMs);
        return <Tag color={st === 'ok' ? 'green' : st === 'inativa' ? 'default' : 'warning'}
                    style={{ marginInlineEnd: 0 }}>{ESTADO_ROTULO[st]}</Tag>;
      } },
    { title: 'Cliente', dataIndex: 'cliente_nome', key: 'cliente', width: 170, ellipsis: true,
      sorter: (a: Estacao, b: Estacao) => txt(a.cliente_nome, b.cliente_nome),
      render: (v: string) => v || <Text type="secondary">—</Text> },
    { title: 'Contrato', dataIndex: 'contrato_numero', key: 'contrato', width: 110,
      sorter: (a: Estacao, b: Estacao) => txt(a.contrato_numero, b.contrato_numero),
      render: (v: string, e: Estacao) => v
        ? <Tooltip title={e.contrato_titulo || undefined}><span>{v}</span></Tooltip>
        : <Text type="secondary">—</Text> },
    { title: 'Corpo hídrico', dataIndex: 'corpo_hidrico', key: 'corpo', width: 150, ellipsis: true,
      sorter: (a: Estacao, b: Estacao) => txt(a.corpo_hidrico, b.corpo_hidrico),
      render: (v: string) => v || <Text type="secondary">—</Text> },
    { title: 'Tipo e forma', key: 'tipo', width: 132,
      sorter: (a: Estacao, b: Estacao) => txt((a.tipos || []).join(), (b.tipos || []).join()),
      render: (_: any, e: Estacao) => (
        <span style={{ fontSize: 11 }}>
          {(e.tipos?.length ? e.tipos : [e.tipo]).filter(Boolean).join(' + ')}
          <div style={{ color: '#8c8c8c' }}>{(e.formas || ['convencional']).join(' + ')}</div>
        </span>
      ) },
    // Os dois números EMPILHADOS, nunca somados: "0 régua · 949.807 telemetria"
    // conta que a automática funciona; "0 · 0" denuncia quem nunca mediu.
    { title: 'Registros', key: 'registros', width: 120, align: 'right' as const,
      sorter: (a: Estacao, b: Estacao) =>
        ((a.leituras_total || 0) + (a.telemetria_total || 0)) - ((b.leituras_total || 0) + (b.telemetria_total || 0)),
      render: (_: any, e: Estacao) => (
        <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
          {(e.leituras_total || 0).toLocaleString('pt-BR')} régua
          <div style={{ color: '#8c8c8c' }}>{(e.telemetria_total || 0).toLocaleString('pt-BR')} telemetria</div>
        </span>
      ) },
    { title: 'Último dado', key: 'ultimo', width: 118,
      sorter: (a: Estacao, b: Estacao) => dt(a.ultimo_dado_em, b.ultimo_dado_em),
      render: (_: any, e: Estacao) => e.ultimo_dado_em
        ? <Tooltip title={dayjs(e.ultimo_dado_em).format('DD/MM/YYYY HH:mm')}>
            <span style={{ fontSize: 12 }}>{idadeTexto(e.ultimo_dado_em, agoraMs)}</span>
            {e.cadencia_seg ? <div style={{ fontSize: 10.5, color: '#8c8c8c' }}>a cada {textoCadencia(e.cadencia_seg)}</div> : null}
          </Tooltip>
        : <Text type="secondary">nunca</Text> },
    // Presença no IoT Core: independe de estar publicando dado (conectado-e-mudo ≠ desligado)
    { title: 'Modem', key: 'modem', width: 110,
      sorter: (a: Estacao, b: Estacao) => Number(a.modem_conectado ?? -1) - Number(b.modem_conectado ?? -1),
      render: (_: any, e: Estacao) => {
      if (e.modem_conectado == null) return <Text type="secondary">—</Text>;
      const quando = e.modem_mudou_em ? dayjs(e.modem_mudou_em).format('DD/MM HH:mm') : '?';
      return e.modem_conectado
        ? <Tooltip title={`Conectado ao IoT Core desde ${quando}`}><Tag color="green">conectado</Tag></Tooltip>
        : <Tooltip title={`Desconectado desde ${quando}${motivoModem(e.modem_motivo) ? ` — ${motivoModem(e.modem_motivo)}` : ''}`}>
            <Tag color="volcano">desconectado</Tag>
          </Tooltip>;
    } },
    { title: 'Pendências', key: 'pend', width: 150,
      sorter: (a: Estacao, b: Estacao) => pendenciasDe(a).length - pendenciasDe(b).length,
      render: (_: any, e: Estacao) => {
        const p = pendenciasDe(e);
        if (!p.length) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        return <span style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {p.map(x => <Tooltip key={x.chave} title={x.dica}>
            <Tag style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '15px' }}>{x.rotulo}</Tag>
          </Tooltip>)}
        </span>;
      } },
    { title: '', key: 'acoes', width: 76, fixed: 'right' as const, render: (_: any, e: Estacao) => (
      <Space size={4}>
        <Tooltip title="Editar"><Button size="small" icon={<EditOutlined />} onClick={() => setCadastro({ open: true, editando: e })} /></Tooltip>
        <Popconfirm title="Excluir estação? As leituras vão junto." okText="Excluir" cancelText="Cancelar" okButtonProps={{ danger: true }} onConfirm={() => excluir(e)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    ) },
  ];

  // Enche o container em ordem de prioridade e para quando não cabe mais.
  // Com 0 (antes da primeira medida) mostra tudo — some coluna DEPOIS de medir
  // é menos ruim do que a tela abrir com 4 colunas e piscar pra 8.
  const colunasVisiveis = useMemo(() => {
    if (!larguraLista) return colunas;
    const porChave = new Map(colunas.map((c: any) => [c.key, c]));
    const orcamento = larguraLista - 2;      // borda da tabela
    let usado = 0;
    const escolhidas: any[] = [];
    for (const chave of PRIORIDADE) {
      const c: any = porChave.get(chave);
      if (!c) continue;
      const w = c.width || 120;
      // As 4 primeiras entram mesmo estourando: uma tabela sem "qual estação"
      // não é tabela. Se estourar, aí sim rola na horizontal.
      if (escolhidas.length < 4 || usado + w <= orcamento) { escolhidas.push(c); usado += w; }
    }
    // Devolve na ORDEM DE LEITURA original, não na de prioridade.
    return colunas.filter((c: any) => escolhidas.includes(c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [larguraLista, agoraMs, estacoes]);

  // Linhas da tabela: cabeçalho de grupo + estações, com a ordenação aplicada
  // DENTRO de cada grupo. Sem agrupamento, é a lista ordenada e pronto.
  const linhasLista = useMemo(() => {
    const col: any = colunas.find((c: any) => c.key === ordem.campo);
    const ordenar = (arr: Estacao[]) => {
      if (!col?.sorter || !ordem.dir) return arr;
      const fora = [...arr].sort(col.sorter);
      return ordem.dir === 'descend' ? fora.reverse() : fora;
    };
    if (agruparPor === 'nenhum') return ordenar(visiveis);
    const out: any[] = [];
    for (const g of agrupar(visiveis, agruparPor, agoraMs)) {
      out.push({ id: `__g__${g.chave}`, _grupo: g });
      out.push(...ordenar(g.itens));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiveis, agruparPor, agoraMs, ordem.campo, ordem.dir]);

  // Envolve cada coluna pra ela saber lidar com a linha de cabeçalho de grupo.
  const colunasTabela = useMemo(() => colunasVisiveis.map((c: any, i: number) => ({
    ...c,
    // `sorter: true` = "o dado vem ordenado de fora". A setinha continua, o
    // antd não reordena nada, e é `linhasLista` quem manda.
    sorter: c.sorter ? true : undefined,
    sortOrder: ordem.campo === c.key ? ordem.dir : null,
    onCell: (r: any) => (r?._grupo
      ? (i === 0 ? { colSpan: colunasVisiveis.length } : { colSpan: 0 })
      : {}),
    render: (v: any, r: any, idx: number) => {
      if (r?._grupo) {
        if (i !== 0) return null;
        const g = r._grupo;
        const acao = g.chave === '_acao';
        return (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '2px 0' }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em',
                           textTransform: 'uppercase', whiteSpace: 'nowrap',
                           color: acao ? '#B07500' : (g.esmaecido ? '#bfbfbf' : '#020D7F') }}>
              {g.titulo}
            </span>
            {g.sub && <Text type="secondary" style={{ fontSize: 11 }} ellipsis>{g.sub}</Text>}
            <span style={{ flex: 1 }} />
            <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{g.itens.length}</Text>
          </div>
        );
      }
      return c.render ? c.render(v, r, idx) : v;
    },
  })), [colunasVisiveis, ordem.campo, ordem.dir]);

  return (
    <div style={{ padding: 16 }}>
      {/* Linha de grupo: fundo levinho e sem hover, pra ela não parecer
          clicável como as linhas de estação. */}
      <style>{`
        .linha-grupo > td { background: #fafbfe !important; border-bottom: 1px solid #e8ecf7 !important; }
        .linha-grupo:hover > td { background: #fafbfe !important; }
      `}</style>
      <Row align="middle" justify="space-between" style={{ marginBottom: 12 }} gutter={[8, 8]}>
        <Col><Title level={4} style={{ margin: 0 }}><EnvironmentOutlined /> Monitoramento Hidrológico</Title></Col>
        <Col>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={carregar}>Atualizar</Button>
            <Button onClick={() => setMolinetesOpen(true)}>🌀 Molinetes</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCadastro({ open: true, editando: null })}>Nova estação</Button>
          </Space>
        </Col>
      </Row>

      {/* KPIs: só o que aponta pra algo a resolver. Um contador que sempre marca
          o mesmo valor não muda decisão nenhuma — por isso não há "total de
          estações" aqui. Cada um FILTRA a lista ao ser clicado, então o número
          nunca é um beco sem saída. Zerado, fica esmaecido e some do caminho. */}
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
        {kpis.map(k => {
          const sel = fKpi === k.chave;
          const vazio = k.valor === 0;
          return (
            <Col key={k.chave} xs={12} sm={8} lg={Math.max(3, Math.floor(24 / kpis.length))} style={{ minWidth: 130 }}>
              <Tooltip title={k.dica}>
                <Card size="small" hoverable={!vazio}
                  onClick={() => !vazio && setFKpi(sel ? null : k.chave)}
                  styles={{ body: { padding: '8px 10px' } }}
                  style={{ cursor: vazio ? 'default' : 'pointer', opacity: vazio ? 0.5 : 1,
                           borderColor: sel ? k.cor : '#f0f0f0',
                           borderWidth: sel ? 2 : 1, background: sel ? '#fafcff' : undefined }}>
                  <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.1, color: vazio ? '#bfbfbf' : k.cor }}>
                    {k.valor}
                  </div>
                  <div style={{ fontSize: 11, color: '#595959', whiteSpace: 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.titulo}</div>
                </Card>
              </Tooltip>
            </Col>
          );
        })}
      </Row>

      {/* Barra de filtros */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <Segmented size="small" value={vista} onChange={(v) => setVista(v as any)}
          options={[{ label: 'Cards', value: 'cards', icon: <AppstoreOutlined /> },
                    { label: 'Lista', value: 'lista', icon: <BarsOutlined /> }]} />
        <Input allowClear size="small" placeholder="Buscar código, nome, rio…" style={{ width: 210 }}
          value={busca} onChange={(e) => setBusca(e.target.value)} />
        <Select size="small" mode="multiple" allowClear maxTagCount="responsive"
          placeholder="Todos os clientes" style={{ minWidth: 200, maxWidth: 360 }}
          value={fCliente} onChange={setFCliente}
          options={clientesComEstacao.map(([id, nome]) => ({ value: id, label: nome }))} />
        <Segmented size="small" value={fStatus} onChange={(v) => setFStatus(v as any)}
          options={[{ label: 'Todas', value: 'todas' }, { label: 'Ativas', value: 'ativa' },
                    { label: 'Inativas', value: 'inativa' }]} />
        {/* Vale nas duas vistas: o agrupamento é o mesmo `agrupar()`, então
            "Precisa de ação" no topo e inativas no fim valem igual em Cards e
            em Lista — a pessoa não precisa reaprender a tela ao trocar. */}
        <Select size="small" style={{ width: 150 }} value={agruparPor} onChange={setAgruparPor}
          options={[{ value: 'cliente', label: 'Agrupar: cliente' },
                    { value: 'contrato', label: 'Agrupar: contrato' },
                    { value: 'nenhum', label: 'Sem agrupar' }]} />
        {filtroAtivo && (
          <Button size="small" type="link" onClick={() => {
            setBusca(''); setFCliente([]); setFStatus('todas'); setFKpi(null);
          }}>limpar filtros</Button>
        )}
        <span style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
          {visiveis.length} de {estacoes.length} · atualizado {dayjs(agoraMs).format('HH:mm')}
        </Text>
      </div>

      {/* Mapa ao LADO da lista em tela larga: o mapa fica parado e só a coluna
          da esquerda rola. Empilha abaixo de lg, e aí SEM altura travada — senão
          vira rolagem dentro de rolagem no celular. As alturas saem de clamp()
          ligado à viewport, nunca de px fixo (CLAUDE.md: nada de rolar tendo
          tela sobrando). */}
      {/* O `div` é quem MEDE (pergunta onde começa e ocupa o resto da tela);
          o Row só herda. Medir o próprio elemento que recebe a altura não
          entra em laço: o topo dele não muda quando a altura muda, porque ele
          é o último bloco da página.
          Abaixo de lg não se trava altura nenhuma — com o mapa empilhado em
          cima, altura fixa vira rolagem dentro de rolagem no celular. */}
      <div ref={areaRef} style={larga ? { height: alturaArea } : undefined}>
      <Row gutter={[16, 16]} style={larga ? { height: '100%' } : undefined}>
        <Col xs={24} lg={vista === 'lista' ? 17 : 15} xl={vista === 'lista' ? 18 : 16}
             style={larga ? { height: '100%' } : undefined}>
        <div ref={listaRef} style={larga ? { height: '100%' } : undefined}>
          {loading && !estacoes.length ? (
            <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
          ) : !visiveis.length ? (
            <Empty style={{ marginTop: 40 }}
              description={estacoes.length ? 'Nenhuma estação neste filtro' : 'Nenhuma estação cadastrada ainda'} />
          ) : vista === 'cards' ? (
            <div style={larga
              ? { height: '100%', overflowY: 'auto', paddingRight: 4 }
              : { maxHeight: 'clamp(320px, 60vh, 900px)', overflowY: 'auto', paddingRight: 4 }}>
              <CardsEstacoes estacoes={visiveis} agruparPor={agruparPor} agoraMs={agoraMs}
                aoAbrir={(e) => setDetalheId(e.id)} />
            </div>
          ) : (
            <Table rowKey="id" size="small" loading={loading} columns={colunasTabela as any} dataSource={linhasLista}
              onChange={(_p, _f, sorter: any) => {
                const s = Array.isArray(sorter) ? sorter[0] : sorter;
                setOrdem({ campo: s?.order ? String(s.columnKey) : null, dir: s?.order ?? null });
              }}
              rowClassName={(r: any) => (r?._grupo ? 'linha-grupo' : '')}
              pagination={false}
              // ⚠️ NÚMERO, não 'max-content'. Com 'max-content' o navegador dá a
              // cada coluna a largura do CONTEÚDO: "CRVR - RIOGRANDENSE
              // VALORIZACAO DE RESIDUO S.A." abria a coluna inteira em vez de
              // truncar nos 170 px, e a tabela rolava na horizontal mesmo em
              // monitor grande. Com um número, as larguras declaradas mandam e o
              // `ellipsis` volta a funcionar. 1054 = a soma no breakpoint lg
              // (piso); quando o xl mostra mais colunas, o antd expande sozinho.
              // Some as larguras do que REALMENTE está na tela. Como a seleção
              // acima já garante que cabe, isso normalmente não gera rolagem —
              // ela só aparece em tela estreita demais para as 4 obrigatórias.
              scroll={{ x: colunasVisiveis.reduce((n: number, c: any) => n + (c.width || 120), 0),
                        y: alturaArea - 47 }}
              locale={{ emptyText: <Empty description="Nenhuma estação neste filtro" /> }} />
          )}
        </div>
        </Col>
        <Col xs={24} lg={vista === 'lista' ? 7 : 9} xl={vista === 'lista' ? 6 : 8}
             style={larga ? { height: '100%' } : undefined}>
          <div style={{ height: larga ? 'calc(100% - 24px)' : 'clamp(220px, 34vh, 380px)',
                        borderRadius: 8, overflow: 'hidden', border: '1px solid #eee' }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6, fontSize: 11, color: '#595959' }}>
            {(['ok', 'silenciosa', 'sem_dado', 'inativa'] as const).map(st => (
              <span key={st} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 9, height: 9, borderRadius: 9, background: ESTADO_COR[st],
                               border: '1.5px solid #fff', boxShadow: '0 0 0 1px #d9d9d9' }} />
                {ESTADO_ROTULO[st]}
              </span>
            ))}
          </div>
        </Col>
      </Row>
      </div>

      {cadastro.open && (
        <ModalCadastroEstacao editando={cadastro.editando} clientes={clientes} contratos={contratos}
          onClose={() => setCadastro({ open: false, editando: null })}
          onSalvo={() => { setCadastro({ open: false, editando: null }); carregar(); }} />
      )}
      {detalheId && (
        <ModalDetalheEstacao id={detalheId} onClose={() => setDetalheId(null)} onMudou={carregar} />
      )}
      {molinetesOpen && <ModalMolinetes onClose={() => setMolinetesOpen(false)} />}
    </div>
  );
}

// ── Modal de cadastro / edição ────────────────────────────────────────────────
function ModalCadastroEstacao({ editando, clientes, contratos, onClose, onSalvo }: {
  editando: Estacao | null; clientes: ClienteOpt[]; contratos: ContratoOpt[]; onClose: () => void; onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  useEffect(() => {
    form.setFieldsValue(editando ? {
      ...editando,
      // Estação anterior à migration pode não ter `tipos` no payload cacheado —
      // cai no escalar pra o campo nunca abrir vazio.
      tipos: (editando.tipos?.length ? editando.tipos : (editando.tipo ? [editando.tipo] : [])),
      formas: editando.formas?.length ? editando.formas : ['convencional'],
      cliente_id: editando.cliente_id ?? undefined, contrato_id: editando.contrato_id ?? undefined,
    } : { tipos: ['fluviometrica'], formas: ['convencional'], unidade: 'm', status: 'ativa' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editando]);

  async function salvar() {
    let vals: any;
    try { vals = await form.validateFields(); } catch { return; }
    setSalvando(true);
    try {
      await api(editando ? 'op_estacao_atualizar' : 'op_estacao_criar', editando ? { id: editando.id, ...vals } : vals);
      message.success(editando ? 'Estação atualizada.' : 'Estação criada.');
      onSalvo();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }

  const formasAtuais = Form.useWatch('formas', form) as string[] | undefined;
  const temTelemetria = (formasAtuais || editando?.formas || []).includes('telemetrica');

  return (
    <Modal open title={editando ? `Editar ${editando.codigo}` : 'Nova estação'} onCancel={onClose}
      onOk={salvar} confirmLoading={salvando} okText="Salvar"
      width="94vw" style={{ maxWidth: editando && temTelemetria ? 1120 : 760, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}>
      <Row gutter={20}>
        <Col xs={24} lg={editando && temTelemetria ? 13 : 24}>
          <Form form={form} layout="vertical" size="small">
            <Row gutter={12}>
              <Col span={8}><Form.Item name="codigo" label="Código" rules={[{ required: true }]}><Input placeholder="RIO-TAQUARA-01" /></Form.Item></Col>
              <Col span={16}><Form.Item name="nome" label="Nome" rules={[{ required: true }]}><Input /></Form.Item></Col>
            </Row>
            <Row gutter={12}>
              {/* Multi-tipo: posto fluviométrico quase sempre tem pluviômetro junto.
                  Hoje a lista é só declaração/classificação — quem passa a LER isto
                  pra decidir abas e seções da ficha são as Fases 2 e 4. */}
              <Col span={8}><Form.Item name="tipos" label="Tipo(s)"
                rules={[{ required: true, message: 'informe ao menos um' }]}
                tooltip="Uma estação pode ser fluviométrica E pluviométrica ao mesmo tempo. Classificação da estação.">
                <Select mode="multiple" allowClear placeholder="Fluviométrica" options={[
                  { value: 'fluviometrica', label: 'Fluviométrica' }, { value: 'pluviometrica', label: 'Pluviométrica' },
                  { value: 'linimetrica', label: 'Linimétrica' }, { value: 'outro', label: 'Outro' }]} /></Form.Item></Col>
              <Col span={10}><Form.Item name="corpo_hidrico" label="Corpo hídrico"><Input placeholder="Rio Taquara" /></Form.Item></Col>
              <Col span={6}><Form.Item name="status" label="Status"><Select options={[{ value: 'ativa', label: 'Ativa' }, { value: 'inativa', label: 'Inativa' }]} /></Form.Item></Col>
            </Row>
            {/* COMO adquire — ortogonal ao que mede. É isto que decide as abas do
                modal: sem 'telemetrica' não existe Configuração; sem 'convencional'
                não existe aba de régua. Multi porque leitura concomitante (régua +
                sensor, pra conferir deriva) é prática padrão em hidrometria. */}
            <Row gutter={12}>
              <Col span={14}><Form.Item name="formas" label="Forma de aquisição"
                rules={[{ required: true, message: 'informe ao menos uma' }]}
                tooltip="Convencional = régua lida por observador. Automática = sensor que grava local (dado entra por importação). Telemétrica = sensor que transmite. Pode ser mais de uma: régua lida junto com sensor é leitura concomitante.">
                <Select mode="multiple" allowClear options={[
                  { value: 'convencional', label: 'Convencional (régua)' },
                  { value: 'automatica', label: 'Automática (sensor local)' },
                  { value: 'telemetrica', label: 'Telemétrica (transmite)' }]} /></Form.Item></Col>
              <Col span={10}>
                {temTelemetria ? (
                  <Form.Item name="transmissao" label="Meio de transmissão"
                    tooltip="IoT Core = modem nosso (LogBox/NOVUS). Satélite = Orbcomm. API = parceiro empurra por HTTP com token.">
                    <Select allowClear placeholder="—" options={[
                      { value: 'iot', label: 'AWS IoT Core (modem próprio)' },
                      { value: 'satelital', label: 'Satélite (Orbcomm)' },
                      { value: 'api', label: 'API de parceiro (HTTP)' }]} />
                  </Form.Item>
                ) : null}
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}><Form.Item name="cliente_id" label="Cliente"><Select allowClear showSearch optionFilterProp="label"
                options={clientes.map(c => ({ value: c.id, label: c.nome_fantasia || c.razao_social || `Cliente ${c.id}` }))} /></Form.Item></Col>
              <Col span={12}><Form.Item name="contrato_id" label="Contrato"><Select allowClear showSearch optionFilterProp="label"
                options={contratos.map(c => ({ value: c.id, label: `${c.numero || '—'} · ${c.titulo || ''}`.trim() }))} /></Form.Item></Col>
            </Row>
            <Row gutter={12}>
              <Col span={8}><Form.Item name="latitude" label="Latitude"><InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.0001} placeholder="-30.03" /></Form.Item></Col>
              <Col span={8}><Form.Item name="longitude" label="Longitude"><InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.0001} placeholder="-51.93" /></Form.Item></Col>
              <Col span={8}><Form.Item name="unidade" label="Unidade"><Select options={[{ value: 'm', label: 'metros (m)' }, { value: 'cm', label: 'centímetros (cm)' }]} /></Form.Item></Col>
            </Row>
            <Row gutter={12}>
              {/* Dois offsets DISTINTOS (Lucas 2026-08-11). Este é o Nº 2 —
                  georref. vertical: altitude do zero da régua no datum oficial.
                  O Nº 1 (sensor→régua, com vigências) mora na seção ao lado. */}
              <Col span={8}><Form.Item name="cota_zero" label="Georref. vertical (datum)"
                tooltip="Altitude do ZERO DA RÉGUA no datum vertical oficial (ex.: Imbituba), obtida quando o RN local é georreferenciado. Altitude do nível = cota local + este valor. NÃO é o offset do sensor — esse tem vigências e fica na seção 'Offset sensor → régua'.">
                <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.001} placeholder="ex.: 33,800" /></Form.Item></Col>
              <Col span={8}><Form.Item name="faixa_min" label="Leitura mín. válida"><InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
              <Col span={8}><Form.Item name="faixa_max" label="Leitura máx. válida"><InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
            </Row>
            <Form.Item name="observacoes" label="Observações"><Input.TextArea rows={2} /></Form.Item>
          </Form>
        </Col>
        {editando && temTelemetria && (
          <Col xs={24} lg={11}>
            <SecaoOffsets estacao={editando} />
          </Col>
        )}
      </Row>
    </Modal>
  );
}

// ── Offset sensor → régua ("zero da régua"), com VIGÊNCIAS ────────────────────
// O sensor telemétrico mede a coluna d'água SOBRE ELE; a régua instalada junto
// é a referência. Este offset leva a leitura do sensor pra escala da régua
// ("cota local") e muda quando o sensor é reinstalado ou deriva — por isso é
// uma SÉRIE de vigências, aplicada na leitura (retroativo imediato, o bruto
// gravado não muda). Não confundir com o georref. vertical (campo no form).
function SecaoOffsets({ estacao }: { estacao: Estacao }) {
  const { message, modal } = AntApp.useApp();
  const [lista, setLista] = useState<any[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  // Variável vem do CATÁLOGO, nunca de texto livre — foi digitar nome à mão
  // que criou o `NivelBat`. O de-para de canais diz quais a estação de fato
  // mede; o resto do catálogo fica num segundo grupo.
  const [opcoesVar, setOpcoesVar] = useState<any[]>([]);
  const [novo, setNovo] = useState<{ variavel: string; offset: number | null; de: Dayjs | null; motivo: string }>(
    { variavel: 'Nivel', offset: null, de: dayjs(), motivo: '' });

  async function carregar() {
    setCarregando(true);
    try { setLista(await api('op_estacao_offsets_listar', { id: estacao.id }) || []); }
    catch { /* seção é acessório do modal — não derruba a edição */ }
    finally { setCarregando(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [estacao.id]);

  useEffect(() => {
    (async () => {
      try {
        // A action é `op_estacao_telemetria_config` (sem `_obter` — esse é o
        // nome da FUNÇÃO no handler, não o da rota). Com o nome errado a
        // chamada dava 400, o catch engolia e o select ficava vazio mostrando
        // só o value cru "Nivel" (Lucas viu em prod, 2026-08-12).
        const r = await api('op_estacao_telemetria_config', { id: estacao.id });
        const cat: any[] = r?.variaveis || [];
        const daEstacao = new Set((r?.canais || [])
          .filter((c: any) => c.variavel && c.ativo).map((c: any) => c.variavel));
        const rot = (v: any) => `${v.codigo}${v.unidade ? ` (${v.unidade})` : ''}`;
        const nesta = cat.filter(v => daEstacao.has(v.codigo));
        const demais = cat.filter(v => !daEstacao.has(v.codigo) && !v.diagnostico);
        const grupos: any[] = [];
        if (nesta.length) grupos.push({ label: 'Medidas nesta estação', options: nesta.map(v => ({ value: v.codigo, label: rot(v) })) });
        if (demais.length) grupos.push({ label: 'Catálogo', options: demais.map(v => ({ value: v.codigo, label: rot(v) })) });
        setOpcoesVar(grupos);
        // Default: a variável de NÍVEL da estação — é o caso normal do offset.
        const nivel = nesta.find(v => v.grandeza_codigo === 'nivel')
          || cat.find(v => v.grandeza_codigo === 'nivel' && v.codigo === 'Nivel');
        if (nivel) setNovo(n => ({ ...n, variavel: nivel.codigo }));
      } catch (e: any) {
        // Catch MUDO escondeu o bug do nome da action. Agora avisa: select
        // vazio sem explicação é pior que erro na tela.
        message.warning('Não consegui carregar o catálogo de variáveis: '
          + (e?.response?.data?.error || e?.message || 'erro desconhecido'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estacao.id]);

  async function criar() {
    if (novo.offset == null || !novo.de) { message.warning('Informe o offset e o início da vigência'); return; }
    setSalvando(true);
    try {
      await api('op_estacao_offset_criar', {
        estacao_id: estacao.id, variavel: novo.variavel || 'Nivel',
        offset_valor: novo.offset, vigente_de: novo.de.toISOString(),
        motivo: novo.motivo || undefined,
      });
      message.success('Vigência registrada — a série já reflete (retroativo à data informada).');
      setNovo({ variavel: 'Nivel', offset: null, de: dayjs(), motivo: '' });
      carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }

  function excluir(o: any) {
    modal.confirm({
      title: 'Excluir esta vigência?',
      content: 'A série volta a usar a vigência anterior (se houver) no período. Use pra corrigir um cadastro errado.',
      okText: 'Excluir', okButtonProps: { danger: true },
      onOk: async () => {
        try { await api('op_estacao_offset_excluir', { id: o.id }); carregar(); }
        catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
      },
    });
  }

  const fmtDt = (v: string | null) => (v ? dayjs(v).format('DD/MM/YY HH:mm') : '—');
  return (
    <div style={{ border: '1px solid #e6e9f2', borderRadius: 8, padding: '10px 12px', background: '#fafbff' }}>
      <div style={{ fontWeight: 600, color: '#020D7F', marginBottom: 2 }}>Offset sensor → régua (cota local)</div>
      <div style={{ fontSize: 11, color: '#777', marginBottom: 8 }}>
        Soma aplicada à leitura do sensor pra levá-la à escala da régua. Vale do início
        informado em diante (retroativo imediato — o bruto gravado não muda); registrar
        uma nova vigência encerra a atual. Reinstalou o sensor ou detectou deriva? Nova vigência.
      </div>
      <Table size="small" rowKey="id" loading={carregando} pagination={false}
        dataSource={lista}
        locale={{ emptyText: 'Sem offset — a série mostra a leitura crua do sensor (coluna d’água)' }}
        columns={[
          { title: 'Variável', dataIndex: 'variavel', width: 76 },
          { title: 'Offset', dataIndex: 'offset_valor', width: 86, align: 'right' as const,
            render: (v: any) => <b>{Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 3 })} {estacao.unidade}</b> },
          { title: 'Vigência', key: 'vig',
            render: (_: any, o: any) => (
              <span style={{ fontSize: 12 }}>
                {fmtDt(o.vigente_de)} → {o.vigente_ate ? fmtDt(o.vigente_ate) : <Tag color="green" style={{ marginInlineStart: 4 }}>atual</Tag>}
                {o.motivo && <div style={{ fontSize: 11, color: '#999' }}>{o.motivo}</div>}
              </span>
            ) },
          { title: '', key: 'x', width: 36,
            render: (_: any, o: any) => <Button size="small" type="text" danger onClick={() => excluir(o)}>✕</Button> },
        ]} />
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 11, color: '#777' }}>Variável</div>
          <Select size="small" style={{ width: 130 }} value={novo.variavel} options={opcoesVar}
            showSearch optionFilterProp="label" placeholder="Nível"
            onChange={v => setNovo(n => ({ ...n, variavel: v }))} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#777' }}>Offset ({estacao.unidade})</div>
          <InputNumber size="small" style={{ width: 100 }} decimalSeparator="," step={0.001}
            placeholder="ex.: 1,235" value={novo.offset}
            onChange={v => setNovo(n => ({ ...n, offset: v as number | null }))} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#777' }}>Vigente desde</div>
          <DatePicker size="small" showTime format="DD/MM/YYYY HH:mm" value={novo.de}
            onChange={v => setNovo(n => ({ ...n, de: v }))} />
        </div>
        <Input size="small" style={{ flex: 1, minWidth: 120 }} placeholder="Motivo (instalação, deriva…)"
          value={novo.motivo} onChange={e => setNovo(n => ({ ...n, motivo: e.target.value }))} />
        <Button size="small" type="primary" loading={salvando} onClick={criar}>Registrar</Button>
      </div>
    </div>
  );
}

// ── Modal de detalhe: gráfico + janela + CSV + QR + leitura ────────────────────
function ModalDetalheEstacao({ id, onClose, onMudou }: { id: string; onClose: () => void; onMudou: () => void }) {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeConsistir = pode('monitoramento.consistencia.usar');
  const podeConfigurarTele = pode('monitoramento.telemetria.configurar');
  // Curar o catálogo é mais restrito que mapear canal: nome novo vale para
  // TODAS as estações, então quem cadastra decide o vocabulário da casa.
  const podeGerenciarVariaveis = pode('monitoramento.variaveis.gerenciar');
  const podeVerFicha = pode('monitoramento.ficha.ver');
  const podePublicarFicha = pode('monitoramento.ficha.publicar');
  const podeEditarFicha = pode('monitoramento.ficha.editar');
  // Importar planilha grava leitura: mesma porta do servidor (op_estacao_leituras_importar).
  const podeImportar = pode('monitoramento.estacoes.gerenciar');
  // ≥ 1200 px: gráfico e painel lateral lado a lado. Abaixo disso, empilha.
  const largo = !!Grid.useBreakpoint().xl;
  const [est, setEst] = useState<Estacao | null>(null);
  const [serie, setSerie] = useState<Leitura[]>([]);
  const [medVazao, setMedVazao] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [preset] = useState<string>('90d');   // janela do gráfico mora no TabSerie
  const [range] = useState<[Dayjs, Dayjs] | null>(null);
  const [novaLeitura, setNovaLeitura] = useState<{ valor: number | null; lido_em: Dayjs | null; obs: string }>({ valor: null, lido_em: dayjs(), obs: '' });
  const [salvandoLeitura, setSalvandoLeitura] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [consOp, setConsOp] = useState<string>('dividir');
  const [consK, setConsK] = useState<number | null>(null);
  const [consBase, setConsBase] = useState<string>('bruto');
  const [consLoad, setConsLoad] = useState(false);
  const [verTabela, setVerTabela] = useState(false);
  // Curva-chave ATIVA da estação: com ela o cotagrama vira hidrograma e as
  // medições de vazão passam a ser comparáveis no MESMO eixo (§6 do doc).
  const [curva, setCurva] = useState<{ a: number; n: number; h0: number; r2: number | null; cota_min: number | null; cota_max: number | null; vigencia_inicio: string | null } | null>(null);

  // Janela ancorada no ÚLTIMO DADO da estação (§5): régua lida a cada 3-7 dias
  // com "7d" contado de agora abre o gráfico vazio quase sempre.
  //
  // ⚠️ Era `est?.ultima_leitura_em`, que o `op_estacao_obter` NUNCA devolveu —
  // a âncora vinha `undefined` e caía em "agora", ou seja, o recurso não valia.
  // Agora o backend manda `ultimo_dado_em` (o maior entre régua e telemetria),
  // que é o certo: numa estação só telemétrica a leitura manual é nula e
  // ancorar nela seria o mesmo que não ancorar.
  const ancora = est?.ultimo_dado_em ?? est?.ultima_leitura_em ?? null;
  const janela = useMemo<{ de: string | null; ate: string | null }>(() => {
    if (range) return { de: range[0].startOf('day').toISOString(), ate: range[1].endOf('day').toISOString() };
    const p = PRESETS.find(x => x.label === preset);
    return janelaAncorada(p ? p.dias : 0, ancora);
  }, [preset, range, ancora]);

  async function carregar() {
    setLoading(true);
    try {
      const [e, s, mv, cv] = await Promise.all([
        api('op_estacao_obter', { id }),
        api('op_estacao_serie', { id, de: janela.de, ate: janela.ate }),
        api('op_vazao_listar', { estacao_id: id }).catch(() => []),
        api('op_curva_listar', { estacao_id: id }).catch(() => []),
      ]);
      setEst(e); setSerie(Array.isArray(s) ? s : []); setMedVazao(Array.isArray(mv) ? mv : []);
      const ativa = (Array.isArray(cv) ? cv : []).find((c: any) => c.ativa);
      // r2 e vigencia_inicio podem ser NULOS numa curva informada por fora.
      // Number(null) é 0 — e "R² 0,0000" se lê como "ajuste não explica nada",
      // que é o oposto de "qualidade desconhecida".
      setCurva(ativa
        ? {
            a: Number(ativa.a), n: Number(ativa.n), h0: Number(ativa.h0),
            r2: ativa.r2 != null ? Number(ativa.r2) : null,
            cota_min: ativa.cota_min != null ? Number(ativa.cota_min) : null,
            cota_max: ativa.cota_max != null ? Number(ativa.cota_max) : null,
            vigencia_inicio: ativa.vigencia_inicio || null,
          }
        : null);
    } catch (err: any) { message.error('Erro: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [id, janela.de, janela.ate]);

  // Medições de vazão dentro da janela (pontos avulsos, esparsos por natureza —
  // campanha de campo). Na vista de vazão elas viram Scatter; na de nível
  // continuam como marca vertical.
  const medicoes = useMemo(() => (medVazao || [])
    .map((m: any) => ({ id: m.id, t: dayjs(m.data_hora).valueOf(), vazao: Number(m.vazao), cota: m.cota != null ? Number(m.cota) : null }))
    .filter((m) => {
      const de = janela.de ? dayjs(janela.de).valueOf() : -Infinity;
      const ate = janela.ate ? dayjs(janela.ate).valueOf() : Infinity;
      return Number.isFinite(m.t) && Number.isFinite(m.vazao) && m.t >= de && m.t <= ate;
    }), [medVazao, janela.de, janela.ate]);


  async function registrarLeitura() {
    if (novaLeitura.valor == null) { message.warning('Informe a leitura.'); return; }
    setSalvandoLeitura(true);
    try {
      await api('op_estacao_leitura_criar', {
        estacao_id: id, valor: novaLeitura.valor,
        lido_em: novaLeitura.lido_em ? novaLeitura.lido_em.toISOString() : null,
        observacao: novaLeitura.obs || null,
      });
      message.success('Leitura registrada.');
      setNovaLeitura({ valor: null, lido_em: dayjs(), obs: '' });
      carregar(); onMudou();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvandoLeitura(false); }
  }

  async function aplicarConsistir() {
    if (consK == null) { message.warning('Informe o valor da operação.'); return; }
    setConsLoad(true);
    try {
      const r = await api('op_estacao_consistir', { estacao_id: id, operacao: consOp, valor: consK, base: consBase, de: janela.de, ate: janela.ate });
      message.success(`${r.afetadas} leitura(s) consistida(s).`);
      carregar(); onMudou();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setConsLoad(false); }
  }
  async function editarConsistido(leituraId: string, v: number | null) {
    try { await api('op_leitura_editar', { id: leituraId, valor_consistido: v }); carregar(); onMudou(); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }
  async function editarBruto(leituraId: string, v: number) {
    try { await api('op_leitura_editar', { id: leituraId, valor: v }); carregar(); onMudou(); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }
  async function excluirLeitura(leituraId: string) {
    try { await api('op_leitura_excluir', { id: leituraId }); message.success('Leitura excluída.'); carregar(); onMudou(); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }
  async function limparSerie() {
    setConsLoad(true);
    try { const r = await api('op_estacao_limpar_leituras', { estacao_id: id }); message.success(`${r.apagadas} leitura(s) apagada(s).`); carregar(); onMudou(); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setConsLoad(false); }
  }

  // Abas por TIPO declarado (multi desde a Fase 1). Estação sem telemetria não
  // ganha aba de modem; pluviométrica pura não ganha aba de vazão.
  const tiposEst = est?.tipos?.length ? est.tipos : (est?.tipo ? [est.tipo] : []);
  const ehFluvial = tiposEst.some(t => t === 'fluviometrica' || t === 'linimetrica');
  // Abas vêm do CADASTRO, não de "tem dado?" — estação recém-instalada não tem
  // leitura nenhuma e mesmo assim precisa das suas abas; e estação que já foi
  // régua e virou automática não deve carregar a aba de régua pra sempre.
  const formasEst: string[] = est?.formas?.length ? est.formas : ['convencional'];
  const ehConvencional = formasEst.includes('convencional');
  const ehAutomatica = formasEst.includes('automatica');
  const ehTelemetrica = formasEst.includes('telemetrica');
  // Série de sensor existe tanto na automática (importada) quanto na telemétrica.
  const temSerieSensor = ehAutomatica || ehTelemetrica;
  const rotuloSensor = ehTelemetrica ? 'Telemetria' : 'Automática';
  const qrUrl = est?.token_hash ? `${window.location.origin}/leitura/${est.token_hash}` : '';
  const [qrPlacaOpen, setQrPlacaOpen] = useState(false);

  // Modal que OCUPA a tela (Lucas, 29/09/2026: "aproveite melhor a tela
  // expandindo o modal"). 96vw até 2200px e quase a altura inteira; o corpo só
  // rola se o conteúdo passar da tela. O que é da ESTAÇÃO (cliente, contrato,
  // cota-zero, leituras, modem) mora na linha das abas — antes eram quatro
  // quadros grandes só dentro de "Convencional", empurrando o gráfico para baixo.
  const infoEstacao = est && (
    <Space size={[6, 4]} wrap style={{ justifyContent: 'flex-end', fontSize: 12, maxWidth: largo ? '62vw' : '100%' }}>
      <Tag style={{ marginInlineEnd: 0 }} title="Cliente">{est.cliente_nome || 'sem cliente'}</Tag>
      <Tag style={{ marginInlineEnd: 0 }} title="Contrato">{est.contrato_numero || 'sem contrato'}</Tag>
      {est.cota_zero != null && (
        <Tag style={{ marginInlineEnd: 0 }} title="Georreferenciamento vertical do zero da régua (datum)">
          cota-zero {Number(est.cota_zero).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} {est.unidade}
        </Tag>
      )}
      {/* Só com o TOTAL vindo do servidor: o tamanho da série é o da janela do
          gráfico, e escrevê-lo como total foi o "51 leituras" de uma estação com 253. */}
      {ehConvencional && est.leituras_total != null && (
        <Tag style={{ marginInlineEnd: 0 }}>{Number(est.leituras_total).toLocaleString('pt-BR')} leituras de régua</Tag>
      )}
      {est.ultimo_dado_em && (
        <Tag style={{ marginInlineEnd: 0 }} title="Último dado recebido (régua ou sensor)">
          último dado {dayjs(est.ultimo_dado_em).format('DD/MM/YY HH:mm')}
        </Tag>
      )}
      {est.modem_conectado != null && (
        <Tooltip title={est.modem_conectado
          ? `Conectado ao IoT Core desde ${dayjs(est.modem_mudou_em).format('DD/MM/YYYY HH:mm')}`
            + (est.modem_quedas_24h ? ` · ${est.modem_quedas_24h} queda(s) nas últimas 24h` : '')
          : `Desconectado desde ${dayjs(est.modem_mudou_em).format('DD/MM/YYYY HH:mm')}`
            + (motivoModem(est.modem_motivo) ? ` — ${motivoModem(est.modem_motivo)}` : '')
            + (est.modem_quedas_24h ? ` · ${est.modem_quedas_24h} queda(s) nas últimas 24h` : '')}>
          <Tag color={est.modem_conectado ? 'success' : 'warning'} style={{ marginInlineEnd: 0 }}>
            {est.modem_conectado ? 'modem conectado' : `modem desconectado desde ${dayjs(est.modem_mudou_em).format('DD/MM HH:mm')}`}
            {est.modem_quedas_24h ? ` · ${est.modem_quedas_24h} queda(s)/24h` : ''}
          </Tag>
        </Tooltip>
      )}
    </Space>
  );
  // Altura do gráfico: a tela menos o que fica acima dele (cabeçalho do modal,
  // abas e a linha de controles). Teto alto para monitor grande não sobrar vazio.
  const alturaSerie = largo ? 'clamp(320px, calc(100vh - 290px), 1100px)' : 'clamp(260px, 55vh, 560px)';

  return (
    <Modal open width="96vw" footer={null} onCancel={onClose}
      style={{ top: 10, maxWidth: 2200, paddingBottom: 0 }}
      styles={{ body: { maxHeight: 'calc(100vh - 88px)', overflowY: 'auto', paddingTop: 0 } }}
      title={est ? <span><EnvironmentOutlined /> {est.codigo} — {est.nome}</span> : 'Estação'}>
      {loading && !est ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : est && (
        <Tabs defaultActiveKey={temSerieSensor ? "telemetria" : "convencional"}
          tabBarExtraContent={largo ? { right: infoEstacao } : undefined}
          items={[
          // TELEMETRIA e CONVENCIONAL usam o MESMO componente de gráfico,
          // trocando só a origem — é o que mantém escala, poda de buracos,
          // unidade, janela ancorada e tooltip idênticos nos dois.
          ...(temSerieSensor ? [{ key: 'telemetria', label: rotuloSensor, children: (
            <>
              {!largo && <div style={{ marginBottom: 8 }}>{infoEstacao}</div>}
              <TabSerie est={est} curva={curva} medicoes={medicoes} origem="telemetria" alturaGrafico={alturaSerie} />
            </>
          ) }] : []),
          ...(!ehConvencional ? [] : [{ key: 'convencional', label: 'Convencional', children: (
        <>
          {!largo && <div style={{ marginBottom: 8 }}>{infoEstacao}</div>}
          {/* Duas colunas a partir de 1200 px: o gráfico fica com a tela e o que
              se faz na estação (registrar leitura, importar, QR) fica ao lado,
              sempre à vista — antes ia para baixo do gráfico e obrigava a rolar. */}
          <div style={{ display: 'flex', flexDirection: largo ? 'row' : 'column', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0, width: largo ? undefined : '100%' }}>
          {/* MESMO gráfico da aba Telemetria, trocando só a origem — escala,
              poda de buracos, unidade, janela ancorada e tooltip idênticos. */}
          <TabSerie est={est} curva={curva} medicoes={medicoes} origem="manual" alturaGrafico={alturaSerie} />

          {/* Consistência escondida até a 2ª etapa (Lucas 2026-08-06) — perm
              monitoramento.consistencia.usar, hoje concedida a ninguém. O código
              fica; ligar de volta é um INSERT em perfil_permissoes, sem deploy. */}
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 10, marginBottom: 12, background: '#fafafa', display: podeConsistir ? undefined : 'none' }}>
            <Text strong>Consistir (corrigir dados)</Text>
            <div style={{ fontSize: 12, color: '#888', margin: '2px 0 8px' }}>
              Mexe só na série <b>consistida</b> — o bruto fica intacto. Aplica na <b>janela selecionada acima</b> (use "Tudo" pra valer pra toda a série).
            </div>
            <Space wrap align="center">
              <Select size="small" value={consOp} onChange={setConsOp} style={{ width: 150 }} options={[
                { value: 'dividir', label: 'Dividir por' }, { value: 'multiplicar', label: 'Multiplicar por' },
                { value: 'somar', label: 'Somar' }, { value: 'definir', label: 'Definir igual a' }]} />
              <InputNumber size="small" value={consK} onChange={(v) => setConsK(v as number)} style={{ width: 120 }} step={0.01} placeholder="valor" />
              <Select size="small" value={consBase} onChange={setConsBase} style={{ width: 175 }} options={[
                { value: 'bruto', label: 'a partir do bruto' }, { value: 'consistido', label: 'do consistido atual' }]} />
              <Button size="small" type="primary" loading={consLoad} onClick={aplicarConsistir}>Aplicar</Button>
              <Button size="small" type="link" onClick={() => setVerTabela((v) => !v)}>
                {verTabela ? 'Ocultar leituras' : `Ver/editar leituras (${est.leituras?.length || 0})`}
              </Button>
              <Popconfirm title="Apagar TODAS as leituras desta estação?" description="Bruto e consistido vão junto. Não dá pra desfazer." okText="Apagar tudo" cancelText="Cancelar" okButtonProps={{ danger: true }} onConfirm={limparSerie}>
                <Button size="small" danger>Limpar série</Button>
              </Popconfirm>
            </Space>
            {verTabela && (
              <Table style={{ marginTop: 10 }} size="small" rowKey="id" dataSource={est.leituras || []} pagination={{ pageSize: 8 }}
                columns={[
                  { title: 'Data/hora', dataIndex: 'lido_em', key: 'd', render: (v: string) => dayjs(v).format('DD/MM/YY HH:mm') },
                  { title: `Bruto (${est.unidade})`, key: 'b', render: (_: any, l: Leitura) => (
                    <InputNumber decimalSeparator="," size="small" key={`b-${l.id}-${l.valor}`} defaultValue={l.valor} step={0.01} style={{ width: 100 }}
                      onBlur={(e) => {
                        const raw = String((e.target as HTMLInputElement).value).replace(',', '.').trim();
                        const nv = parseFloat(raw);
                        if (Number.isFinite(nv) && nv !== Number(l.valor)) editarBruto(l.id, nv);
                      }} />
                  ) },
                  { title: `Consistido (${est.unidade})`, key: 'c', render: (_: any, l: Leitura) => (
                    <InputNumber decimalSeparator="," size="small" key={`${l.id}-${l.valor_consistido ?? 'n'}`} defaultValue={l.valor_consistido ?? undefined}
                      step={0.01} style={{ width: 110 }} placeholder={nf(l.valor)}
                      onBlur={(e) => {
                        const raw = String((e.target as HTMLInputElement).value).replace(',', '.').trim();
                        const nv = raw === '' ? null : parseFloat(raw);
                        if ((nv ?? null) !== (l.valor_consistido ?? null)) editarConsistido(l.id, Number.isFinite(nv as number) ? (nv as number) : null);
                      }} />
                  ) },
                  { title: '', key: 'x', width: 40, render: (_: any, l: Leitura) => (
                    <Popconfirm title="Excluir esta leitura?" okText="Excluir" cancelText="Cancelar" okButtonProps={{ danger: true }} onConfirm={() => excluirLeitura(l.id)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  ) },
                ] as any} />
            )}
          </div>

          </div>

          <div style={{ width: largo ? 320 : '100%', flexShrink: 0, display: 'flex', flexDirection: largo ? 'column' : 'row', flexWrap: 'wrap', gap: 12 }}>
            <Card size="small" title="Registrar leitura" style={{ flex: largo ? undefined : '1 1 320px' }}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Leitura ({est.unidade})</Text>
                  <InputNumber decimalSeparator="," value={novaLeitura.valor} step={0.01} style={{ width: '100%' }}
                    onChange={(v) => setNovaLeitura(s => ({ ...s, valor: v as number }))} />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Horário</Text>
                  <DatePicker showTime value={novaLeitura.lido_em} format="DD/MM/YYYY HH:mm" style={{ width: '100%' }}
                    onChange={(v) => setNovaLeitura(s => ({ ...s, lido_em: v }))} />
                </div>
                <Input placeholder="Observação (opcional)" value={novaLeitura.obs}
                  onChange={(e) => setNovaLeitura(s => ({ ...s, obs: e.target.value }))} />
                <Button type="primary" block loading={salvandoLeitura} onClick={registrarLeitura}>Adicionar leitura</Button>
                {/* O botão tinha sumido em 06/08 (reorganização das abas) e a importação
                    ficou sem porta. Voltou com a planilha padrão (28/09/2026). */}
                {podeImportar && (
                  <Button block icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
                    Importar planilha (Excel)
                  </Button>
                )}
              </Space>
            </Card>
            <Card size="small" title={<span><QrcodeOutlined /> QR da estação</span>}
              style={{ flex: largo ? undefined : '1 1 240px' }} styles={{ body: { textAlign: 'center' } }}>
              {qrUrl ? <>
                {/* level="H" (30% de correção) igual ao da placa impressa: se a
                    tela mostrasse "M" e a placa saísse "H", o QR conferido aqui
                    não seria o mesmo desenho que vai pro vinil. */}
                <QRCodeCanvas value={qrUrl} size={128} level="H" includeMargin />
                <Typography.Paragraph copyable={{ text: qrUrl, tooltips: ['Copiar link', 'Copiado'] }}
                  style={{ fontSize: 10, color: '#999', margin: '2px 0 6px', wordBreak: 'break-all' }}>
                  {qrUrl}
                </Typography.Paragraph>
                <Button size="small" icon={<FilePdfOutlined />} onClick={() => setQrPlacaOpen(true)}>PDF para a gráfica</Button>
              </> : <Text type="secondary">Sem token</Text>}
            </Card>
          </div>
          </div>
          {qrPlacaOpen && <ModalQrPlaca estacaoId={id} codigo={est.codigo} onClose={() => setQrPlacaOpen(false)} />}
          {importOpen && <ImportarPlanilhaLeituras
            estacao={{ id, codigo: est.codigo, nome: est.nome, unidade: est.unidade, tipos: tiposEst }}
            onClose={() => setImportOpen(false)}
            onImportado={() => { setImportOpen(false); carregar(); onMudou(); }} />}
        </>
          ) }]),
          // Vazão é ferramenta de estação fluviométrica — some numa pluviométrica
          // pura, onde as abas ficavam vazias sem explicar por quê.
          ...(ehFluvial ? [{ key: 'curva', label: 'Curva-chave', children: <TabVazao estacaoId={id} unidade={est.unidade} codigo={est.codigo} nome={est.nome} /> }] : []),
          // Ficha ANA: existe pra qualquer tipo (o que muda são as seções lá
          // dentro). Some inteira pra quem não tem `monitoramento.ficha.ver`.
          ...(podeVerFicha ? [{ key: 'ficha', label: 'Ficha', children: (
            <TabFicha estacaoId={id} tipos={tiposEst} podeEditar={podeEditarFicha} podePublicar={podePublicarFicha} />
          ) }] : []),
          // Ferramentas de consistência ficam atrás da MESMA permissão que as
          // esconde nas outras abas — o código segue vivo pra 2ª etapa.
          ...(temSerieSensor && podeConsistir ? [{ key: 'ferramentas', label: 'Ferramentas', children: <TabTelemetria estacaoId={id} codigo={est.codigo} /> }] : []),
          // Configuração é do MODEM: estação convencional pura não tem o que
          // configurar, e a aba só confundia (era o incômodo do Lucas).
          ...(ehTelemetrica ? [{ key: 'config', label: 'Configuração', children: (
            <TabConfigTelemetria est={est} podeConfigurar={podeConfigurarTele}
              podeGerenciarVariaveis={podeGerenciarVariaveis}
              transmissao={est.transmissao || null} />
          ) }] : []),
        ]} />
      )}
    </Modal>
  );
}

// ── Aba TELEMETRIA (F2.1) — série multi-variável + consistência (espelha o Nível) ──
// Resolução × agregação (à la Grafana) + grade que revela gaps + consistir/import.
function TabTelemetria({ estacaoId, codigo }: { estacaoId: string; codigo: string }) {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeConsistir = pode('monitoramento.consistencia.usar');
  const [vars, setVars] = useState<{ variavel: string; n: number; n_consistido: number; de: string; ate: string }[]>([]);
  const [variavel, setVariavel] = useState<string>('');
  const [preset, setPreset] = useState<string>('Tudo');
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [resolucao, setResolucao] = useState<string>('auto');
  const [agregacao, setAgregacao] = useState<string>('media');
  const [serie, setSerie] = useState<{ lido_em: string; valor: number | null; valor_consistido: number | null; n: number }[]>([]);
  const [meta, setMeta] = useState<{ total: number; cadencia_seg: number | null; resolucao_seg: number | null; buckets: number }>({ total: 0, cadencia_seg: null, resolucao_seg: null, buckets: 0 });
  const [loadingVars, setLoadingVars] = useState(true);
  const [loading, setLoading] = useState(false);
  const [consOp, setConsOp] = useState('multiplicar');
  const [consK, setConsK] = useState<number | null>(null);
  const [consBase, setConsBase] = useState('bruto');
  const [consLoad, setConsLoad] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Âncora da janela = última leitura DA VARIÁVEL selecionada (vem do
  // /vars). Sem isso, "7d" contado de agora numa série que parou semanas
  // atrás abre o gráfico vazio — o operador acha que o sistema perdeu o dado.
  const ancora = useMemo(() => vars.find(v => v.variavel === variavel)?.ate ?? null, [vars, variavel]);
  const janela = useMemo<{ de: string | null; ate: string | null }>(() => {
    if (range) return { de: range[0].startOf('day').toISOString(), ate: range[1].endOf('day').toISOString() };
    const p = PRESETS.find(x => x.label === preset);
    return janelaAncorada(p ? p.dias : 0, ancora);
  }, [preset, range, ancora]);

  useEffect(() => {
    (async () => {
      setLoadingVars(true);
      try {
        const v = await api('op_estacao_telemetria_vars', { id: estacaoId });
        const arr = Array.isArray(v) ? v : [];
        // "Nivel" no topo e como default (senão abriria numa série quase toda zero, ex: chuva).
        arr.sort((a: any, b: any) => (a.variavel === 'Nivel' ? -1 : b.variavel === 'Nivel' ? 1 : 0));
        setVars(arr);
        if (arr.length) { setVariavel(arr[0].variavel); setAgregacao(aggDefault(arr[0].variavel)); }
      } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
      finally { setLoadingVars(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estacaoId]);

  const carregar = async () => {
    if (!variavel) return;
    setLoading(true);
    try {
      const r = await api('op_estacao_telemetria_serie', { id: estacaoId, variavel, de: janela.de, ate: janela.ate, resolucao, agregacao });
      setSerie(Array.isArray(r?.pontos) ? r.pontos : []);
      setMeta({ total: Number(r?.total) || 0, cadencia_seg: r?.cadencia_seg ?? null, resolucao_seg: r?.resolucao_seg ?? null, buckets: Number(r?.buckets) || 0 });
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [estacaoId, variavel, janela.de, janela.ate, resolucao, agregacao]);

  // Eixo X passou a ser NUMÉRICO (tempo): o categórico por rótulo espaçava
  // igual casas de 1 min e paradas de 30 h, e a poda de buracos precisa de `t`.
  const dadosBrutos = useMemo(() => serie.map(l => ({
    t: dayjs(l.lido_em).valueOf(),
    valor: l.valor != null ? Number(l.valor) : null,
    consistido: l.valor_consistido != null ? Number(l.valor_consistido) : null,
  })), [serie]);
  // Poda das casas vazias da grade (só quando HÁ grade — série sem resolução
  // não é bucketizada e passa intacta). Mantém UMA quebra nos buracos grandes.
  const dados = useMemo(() => (
    meta.resolucao_seg
      ? podarBuracos(dadosBrutos, limiarBuracoMs(meta.resolucao_seg))
      : dadosBrutos
  ), [dadosBrutos, meta.resolucao_seg]);
  const temConsistido = useMemo(() => dados.some(d => d.consistido != null), [dados]);

  const grandeza = useMemo(() => grandezaDe(variavel), [variavel]);
  const barras = useMemo(() => ehBarras(variavel), [variavel]);
  const dominio = useMemo(() => dominioY(
    dados.flatMap(d => [d.valor, d.consistido]), grandeza, barras), [dados, grandeza, barras]);
  // Ponto marcado quando a série é esparsa. Conta pontos COM VALOR, não casas
  // da grade: 24 h de grade de 1 min são 1.440 casas mesmo com 90 medições.
  const pontosReais = useMemo(() => dados.reduce(
    (n, p) => n + (p.valor != null || p.consistido != null ? 1 : 0), 0), [dados]);
  const pontuada = pontosReais > 0 && pontosReais <= 150;

  async function baixarTudo() {
    try {
      message.loading({ content: 'Montando o Excel…', key: 'dl' });
      const r = await api('op_estacao_telemetria_serie', { id: estacaoId, variavel, de: janela.de, ate: janela.ate, resolucao, agregacao, completo: true });
      const pts = Array.isArray(r?.pontos) ? r.pontos : [];
      const cab = ['lido_em', 'variavel', 'bruto', 'consistido', 'n'];
      // Vírgula decimal (BR) + sem o "1.00000000000000000000" do numeric do PG, que o
      // Excel pt-BR lê como 1e20 (ponto vira separador de milhar). Number normaliza.
      const fmtN = (v: any) => (v == null || v === '') ? '' : String(+Number(v).toFixed(6)).replace('.', ',');
      const linhas = pts.map((l: any) => [dayjs(l.lido_em).format('YYYY-MM-DD HH:mm:ss'), variavel, fmtN(l.valor), fmtN(l.valor_consistido), l.n].join(';'));
      const csv = '﻿' + [cab.join(';'), ...linhas].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `telemetria-${codigo}-${variavel}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      message.success({ content: `${pts.length.toLocaleString('pt-BR')} linhas (gaps em branco)`, key: 'dl' });
    } catch (e: any) { message.error({ content: 'Erro: ' + (e.response?.data?.error || e.message), key: 'dl' }); }
  }

  async function aplicarConsistir() {
    if (consK == null) { message.warning('Informe o valor da operação.'); return; }
    setConsLoad(true);
    try {
      const r = await api('op_estacao_telemetria_consistir', { estacao_id: estacaoId, variavel, operacao: consOp, valor: consK, base: consBase, de: janela.de, ate: janela.ate });
      message.success(`${r.afetadas} ponto(s) consistido(s).`);
      carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setConsLoad(false); }
  }

  if (loadingVars) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  if (!vars.length) return <Empty description="Esta estação não tem dados de telemetria" style={{ padding: 40 }} />;

  return (
    <>
      <Space style={{ marginBottom: 8 }} wrap>
        <Select size="small" value={variavel} style={{ minWidth: 180 }}
          onChange={(v) => { setVariavel(v); setAgregacao(aggDefault(v)); }}
          options={vars.map(v => ({ value: v.variavel, label: `${v.variavel} (${v.n.toLocaleString('pt-BR')}${v.n_consistido ? ' · ' + v.n_consistido.toLocaleString('pt-BR') + ' cons' : ''})` }))} />
        <Segmented value={range ? '' : preset} options={PRESETS.map(p => p.label)} onChange={(v) => { setRange(null); setPreset(String(v)); }} />
        <DatePicker.RangePicker size="small" value={range as any} onChange={(v) => setRange(v as any)} allowClear />
      </Space>
      <Space style={{ marginBottom: 6 }} wrap>
        <Text type="secondary" style={{ fontSize: 12 }}>Resolução</Text>
        <Select size="small" value={resolucao} onChange={setResolucao} style={{ width: 92 }} options={TELE_RES} />
        <Text type="secondary" style={{ fontSize: 12 }}>Agregação</Text>
        <Select size="small" value={agregacao} onChange={setAgregacao} style={{ width: 108 }} options={TELE_AGG} />
        <Button size="small" icon={<DownloadOutlined />} onClick={baixarTudo}>Baixar tudo (Excel)</Button>
        {podeConsistir && (
          <Button size="small" icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>Importar consistido</Button>
        )}
      </Space>
      <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>
        {meta.total.toLocaleString('pt-BR')} pts · cadência ~{fmtCadencia(meta.cadencia_seg)} · grade {fmtCadencia(meta.resolucao_seg)} ({meta.buckets.toLocaleString('pt-BR')} buckets)
        {' · '}{pontosReais.toLocaleString('pt-BR')} no gráfico
      </div>
      {/* Sem este aviso, "7d" ancorado numa série parada há semanas mostra um
          pedaço do passado com cara de dado atual. */}
      {!range && ancoraDefasada(ancora) && (
        <div style={{ fontSize: 11, color: '#B07500', marginBottom: 6 }}>
          ⏸ período ancorado na última leitura ({dayjs(ancora).format('DD/MM/YYYY HH:mm')}) — sem dados novos desde então
        </div>
      )}
      <div style={{ height: 300, marginBottom: 12 }}>
        {loading ? <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div> :
         dados.length === 0 ? <Empty description="Sem dados nesta janela" style={{ paddingTop: 70 }} /> : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dados} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                tickFormatter={(v: number) => dayjs(v).format('DD/MM HH:mm')} tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis tick={{ fontSize: 10 }} domain={dominio} />
              <RTooltip labelFormatter={(v: any) => dayjs(Number(v)).format('DD/MM/YY HH:mm')}
                formatter={(v: any) => fmtValor(v, variavel)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {/* Chuva é hietograma (barra do chão), não linha — ligar pontos de
                  chuva com linha é errado de leitura. */}
              {barras ? (
                <>
                  <Bar dataKey="valor" name="Bruto" fill={temConsistido ? '#b0b8cc' : '#0930D0'} isAnimationActive={false} />
                  {temConsistido && <Bar dataKey="consistido" name="Consistido" fill="#0a7f4a" isAnimationActive={false} />}
                </>
              ) : (
                <>
                  <Line type="monotone" dataKey="valor" name="Bruto" stroke={temConsistido ? '#b0b8cc' : '#0930D0'} dot={pontuada ? { r: 2 } : false} strokeWidth={temConsistido ? 1.3 : 1.8} isAnimationActive={false} connectNulls={false} />
                  {temConsistido && <Line type="monotone" dataKey="consistido" name="Consistido" stroke="#0a7f4a" dot={pontuada ? { r: 2 } : false} strokeWidth={1.8} isAnimationActive={false} connectNulls={false} />}
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      {/* Escondida junto com a da aba Nível — mesma permissão. */}
      <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 10, background: '#fafafa', display: podeConsistir ? undefined : 'none' }}>
        <Text strong style={{ fontSize: 13 }}>Consistir {variavel}</Text>
        <span style={{ fontSize: 12, color: '#888' }}> — mexe só na série consistida, na janela acima (use "Tudo" pra valer pra tudo).</span>
        <Space wrap align="center" style={{ marginTop: 8 }}>
          <Select size="small" value={consOp} onChange={setConsOp} style={{ width: 145 }} options={[
            { value: 'multiplicar', label: 'Multiplicar por' }, { value: 'dividir', label: 'Dividir por' },
            { value: 'somar', label: 'Somar' }, { value: 'definir', label: 'Definir igual a' }]} />
          <InputNumber size="small" value={consK} onChange={(v) => setConsK(v as number)} style={{ width: 120 }} step={0.01} placeholder="valor" />
          <Select size="small" value={consBase} onChange={setConsBase} style={{ width: 170 }} options={[
            { value: 'bruto', label: 'a partir do bruto' }, { value: 'consistido', label: 'do consistido atual' }]} />
          <Popconfirm title={`Aplicar em ${variavel} na janela selecionada?`} onConfirm={aplicarConsistir} okText="Aplicar" cancelText="Cancelar">
            <Button size="small" type="primary" loading={consLoad}>Aplicar</Button>
          </Popconfirm>
        </Space>
      </div>
      {importOpen && <ModalImportarTelemetria estacaoId={estacaoId} variavel={variavel} codigo={codigo}
        onClose={() => setImportOpen(false)} onImportado={() => { setImportOpen(false); carregar(); }} />}
    </>
  );
}

// Importar série CONSISTIDA do Excel (por variável) — casa por data/hora; data nova preenche gap.
function ModalImportarTelemetria({ estacaoId, variavel, codigo, onClose, onImportado }: {
  estacaoId: string; variavel: string; codigo: string; onClose: () => void; onImportado: () => void;
}) {
  const { message } = AntApp.useApp();
  const [linhas, setLinhas] = useState<Record<string, any>[]>([]);
  const [colunas, setColunas] = useState<string[]>([]);
  const [colData, setColData] = useState<string | undefined>();
  const [colValor, setColValor] = useState<string | undefined>();
  const [fileName, setFileName] = useState('');
  const [importando, setImportando] = useState(false);

  async function lerArquivo(file: File) {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json: Record<string, any>[] = XLSX.utils.sheet_to_json(ws, { defval: null });
      if (!json.length) { message.warning('Planilha vazia'); return; }
      const cols = Object.keys(json[0]);
      setLinhas(json); setColunas(cols); setFileName(file.name);
      const acharData = cols.find(c => /lido_em|data|hora|date|time/i.test(c)) || cols.find(c => parseLidoEmCelula(json[0][c]) != null);
      const acharValor = cols.find(c => /consist|ajust|valor|value/i.test(c)) || cols.find(c => c !== acharData && parseValorCelula(json[0][c]) != null);
      setColData(acharData); setColValor(acharValor);
    } catch (e: any) { message.error('Erro ao ler arquivo: ' + e.message); }
  }

  const previa = useMemo(() => {
    if (!colData || !colValor) return { validas: [] as any[], total: linhas.length };
    const validas = linhas.map(l => ({ lido_em: parseLidoEmCelula(l[colData]), valor: parseValorCelula(l[colValor]) }))
      .filter(x => x.lido_em && x.valor !== null);
    return { validas, total: linhas.length };
  }, [linhas, colData, colValor]);

  async function importar() {
    if (!previa.validas.length) { message.warning('Nenhuma linha válida.'); return; }
    setImportando(true);
    try {
      const r = await api('op_estacao_telemetria_importar', { estacao_id: estacaoId, variavel, pontos: previa.validas });
      message.success(`${r.atualizados} atualizado(s), ${r.gaps_criados} gap(s) preenchido(s)${r.ignoradas ? `, ${r.ignoradas} ignorada(s)` : ''}.`);
      onImportado();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setImportando(false); }
  }

  const opts = colunas.map(c => ({ value: c, label: c }));
  return (
    <Modal open title={`Importar consistido — ${variavel}`} onCancel={onClose} width={520}
      okText={`Importar ${previa.validas.length || ''}`.trim()} okButtonProps={{ disabled: !previa.validas.length }}
      confirmLoading={importando} onOk={importar}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
        Sobe a série ajustada (do Excel) de <b>{variavel}</b> em {codigo}. Casa por data/hora: ponto existente recebe o valor consistido; data nova preenche o gap (bruto fica vazio).
      </div>
      <input type="file" accept=".xlsx,.xls,.csv" style={{ marginBottom: 12 }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) lerArquivo(f); }} />
      {linhas.length > 0 && (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>{fileName} — {linhas.length} linha(s). Qual coluna é o quê:</Text>
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={12}><Text style={{ fontSize: 12 }}>Data/hora *</Text><Select size="small" style={{ width: '100%' }} value={colData} onChange={setColData} options={opts} placeholder="coluna" /></Col>
            <Col span={12}><Text style={{ fontSize: 12 }}>Valor consistido *</Text><Select size="small" style={{ width: '100%' }} value={colValor} onChange={setColValor} options={opts} placeholder="coluna" /></Col>
          </Row>
          <div style={{ marginTop: 12, padding: 8, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
            <Text>{previa.validas.length}</Text> de <Text>{previa.total}</Text> linha(s) válida(s)
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Curva-chave informada por fora ────────────────────────────────────────────
// Q = a·(h − h0)^n com os parâmetros vindos de planilha / software de hidrometria
// / estudo de terceiro. O backend (op_curva_salvar) sempre aceitou isto — só
// exige a, n, h0 e nunca lê as medições; faltava a porta no front.
function ModalCurvaManual({ unidade, salvando, inicial, onFechar, onSalvar }: {
  unidade: string; salvando: boolean; inicial: any | null;
  onFechar: () => void; onSalvar: (v: any) => void;
}) {
  const [form] = Form.useForm();
  return (
    <Modal open title="Informar curva-chave" width={560} onCancel={onFechar}
      okText="Salvar curva" confirmLoading={salvando}
      onOk={() => form.validateFields().then(onSalvar)}>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Salvar arquiva a curva ativa anterior"
        description="O histórico é preservado — a curva anterior deixa de valer, mas não é apagada. O hidrograma da aba Série e o portal do cliente passam a usar esta." />
      <Form form={form} layout="vertical" size="small"
        initialValues={inicial ? {
          a: Number(inicial.a), n: Number(inicial.n), h0: Number(inicial.h0),
          r2: inicial.r2 != null ? Number(inicial.r2) : undefined,
          cota_min: inicial.cota_min != null ? Number(inicial.cota_min) : undefined,
          cota_max: inicial.cota_max != null ? Number(inicial.cota_max) : undefined,
          // Sem recarregar estes dois, reabrir o modal só pra corrigir o h0
          // apagava a procedência: a curva nova nascia sem origem e sem vigência.
          vigencia_inicio: inicial.vigencia_inicio ? dayjs(inicial.vigencia_inicio) : undefined,
          observacao: inicial.observacao || undefined,
        } : undefined}>
        <div style={{ background: '#f7f9ff', border: '1px solid #e3e9ff', borderRadius: 6, padding: '8px 10px', marginBottom: 12, textAlign: 'center' }}>
          <Text style={{ fontSize: 15 }}>Q = a·(h − h₀)<sup>n</sup></Text>
        </div>
        <Row gutter={10}>
          <Col span={8}><Form.Item name="a" label="a (coeficiente)" rules={[{ required: true, message: 'obrigatório' }]}>
            <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.0001} placeholder="ex: 12,3456" /></Form.Item></Col>
          <Col span={8}><Form.Item name="n" label="n (expoente)" rules={[{ required: true, message: 'obrigatório' }]}>
            <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.001} placeholder="ex: 1,85" /></Form.Item></Col>
          <Col span={8}><Form.Item name="h0" label={`h₀ (${unidade})`} rules={[{ required: true, message: 'obrigatório' }]}
            tooltip="Cota de escoamento nulo. Abaixo dela a vazão é zero.">
            <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.001} placeholder="ex: 0,150" /></Form.Item></Col>
        </Row>
        <Row gutter={10}>
          <Col span={8}><Form.Item name="r2" label="R² (opcional)"
            tooltip="Se o ajuste externo reportou o coeficiente de determinação.">
            <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.0001} min={0} max={1} /></Form.Item></Col>
          <Col span={8}><Form.Item name="cota_min" label={`Cota mín. (${unidade})`}
            tooltip="Faixa em que a curva foi ajustada — define o trecho desenhado no gráfico e alerta sobre extrapolação.">
            <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
          <Col span={8}><Form.Item name="cota_max" label={`Cota máx. (${unidade})`}>
            <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
        </Row>
        <Form.Item name="vigencia_inicio" label="Vigente desde (opcional)"
          tooltip="Data a partir da qual esta curva vale. Útil quando a seção mudou (assoreamento, obra).">
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="observacao" label="Origem / observação"
          tooltip="De onde veio a curva — vira o registro de procedência.">
          <Input.TextArea rows={2} placeholder="ex: ajuste do relatório R03 da campanha 2025, 18 medições, planilha do Alexandre" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Aba VAZÃO — curva-chave (cota × vazão) + medições + import (V1) ────────────
function TabVazao({ estacaoId, unidade, codigo, nome }: { estacaoId: string; unidade: string; codigo: string; nome: string }) {
  const { message } = AntApp.useApp();
  // ≥ 1200 px: curva à esquerda, medições ao lado (antes tudo empilhado, com o
  // gráfico preso em 280 px e a tabela abaixo da dobra).
  const largo = !!Grid.useBreakpoint().xl;
  const [medicoes, setMedicoes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [nova, setNova] = useState<{ data: Dayjs | null; cota: number | null; vazao: number | null }>({ data: dayjs(), cota: null, vazao: null });
  const [salvando, setSalvando] = useState(false);
  const { pode } = usePerms();
  const podeEditar = pode('monitoramento.medicoes.editar');
  const [editando, setEditando] = useState<any | null>(null);
  const [editData, setEditData] = useState<Dayjs | null>(null);
  const [salvandoEdit, setSalvandoEdit] = useState(false);
  const [detalhe, setDetalhe] = useState<any | null>(null);
  const [curvaSalva, setCurvaSalva] = useState<any | null>(null);
  const [cotaConv, setCotaConv] = useState<number | null>(null);
  const [salvandoCurva, setSalvandoCurva] = useState(false);
  const [curvaManualOpen, setCurvaManualOpen] = useState(false);
  const podeGerenciar = pode('monitoramento.leituras.gerenciar');

  async function carregar() {
    setLoading(true);
    try {
      const [d, cc] = await Promise.all([
        api('op_vazao_listar', { estacao_id: estacaoId }),
        api('op_curva_listar', { estacao_id: estacaoId }).catch(() => []),
      ]);
      setMedicoes(Array.isArray(d) ? d : []);
      setCurvaSalva(Array.isArray(cc) ? (cc.find((x: any) => x.ativa) || null) : null);
    }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [estacaoId]);

  async function adicionar() {
    if (nova.vazao == null) { message.warning('Informe a vazão.'); return; }
    setSalvando(true);
    try {
      await api('op_vazao_criar', { estacao_id: estacaoId, vazao: nova.vazao, cota: nova.cota, data_hora: nova.data ? nova.data.toISOString() : null, metodo: 'manual' });
      message.success('Medição adicionada.');
      setNova({ data: dayjs(), cota: null, vazao: null }); carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }
  async function excluir(idm: string) {
    try { await api('op_vazao_excluir', { id: idm }); carregar(); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }
  function abrirEdicao(m: any) { setEditando(m); setEditData(m.data_hora ? dayjs(m.data_hora) : dayjs()); }
  async function salvarEdicao() {
    if (!editando || !editData) return;
    setSalvandoEdit(true);
    try {
      await api('op_vazao_editar', { id: editando.id, data_hora: editData.toISOString() });
      message.success('Data corrigida.');
      setEditando(null); carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvandoEdit(false); }
  }

  const pontos = useMemo(() => medicoes.filter(m => m.cota != null && m.vazao != null)
    .map(m => ({ vazao: Number(m.vazao), cota: Number(m.cota) })), [medicoes]);

  const ajuste = useMemo(() => ajustarPotencia(pontos), [pontos]);
  const curvaAtiva = useMemo(() => (curvaSalva
    ? {
        a: Number(curvaSalva.a), n: Number(curvaSalva.n), h0: Number(curvaSalva.h0),
        r2: curvaSalva.r2 != null ? Number(curvaSalva.r2) : null,
        cota_min: curvaSalva.cota_min != null ? Number(curvaSalva.cota_min) : null,
        cota_max: curvaSalva.cota_max != null ? Number(curvaSalva.cota_max) : null,
        // n_pontos NULO é o discriminador da curva informada por fora
        n_pontos: curvaSalva.n_pontos != null ? Number(curvaSalva.n_pontos) : null,
        observacao: curvaSalva.observacao || null,
        vigencia_inicio: curvaSalva.vigencia_inicio || null,
      }
    : (ajuste ? { ...ajuste, cota_min: null, cota_max: null, n_pontos: pontos.length, observacao: null, vigencia_inicio: null } : null)
  ), [curvaSalva, ajuste, pontos.length]);
  // Curva informada manualmente pode existir SEM nenhuma medição — nesse caso a
  // faixa de traçado vem da própria curva (cota_min/cota_max), não dos pontos.
  const curvaPts = useMemo(() => {
    if (!curvaAtiva) return [] as { cota: number; vazao: number }[];
    let hmin: number | null = curvaAtiva.cota_min;
    let hmax: number | null = curvaAtiva.cota_max;
    if (hmin == null || hmax == null || !(hmax > hmin)) {
      if (pontos.length < 2) return [];
      const cs = pontos.map(p => p.cota); hmin = Math.min(...cs); hmax = Math.max(...cs);
    }
    const arr: { cota: number; vazao: number }[] = [];
    for (let i = 0; i <= 40; i++) {
      const h = hmin + (i / 40) * (hmax - hmin); const dh = h - curvaAtiva.h0;
      if (dh > 0) arr.push({ cota: h, vazao: curvaAtiva.a * Math.pow(dh, curvaAtiva.n) });
    }
    return arr;
  }, [curvaAtiva, pontos]);
  const vazaoConv = (cotaConv != null && curvaAtiva && (cotaConv - curvaAtiva.h0) > 0)
    ? curvaAtiva.a * Math.pow(cotaConv - curvaAtiva.h0, curvaAtiva.n) : null;

  async function salvarCurva() {
    if (!ajuste) { message.warning('Preciso de ao menos 3 medições com cota + vazão.'); return; }
    setSalvandoCurva(true);
    try {
      const cs = pontos.map(p => p.cota);
      await api('op_curva_salvar', {
        estacao_id: estacaoId, a: ajuste.a, n: ajuste.n, h0: ajuste.h0, r2: ajuste.r2,
        cota_min: Math.min(...cs), cota_max: Math.max(...cs), n_pontos: pontos.length,
      });
      message.success('Curva-chave salva.');
      carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvandoCurva(false); }
  }

  // Curva ajustada FORA do sistema (planilha, software de hidrometria, estudo de
  // terceiro). O backend já aceitava — só exige a/n/h0 e nunca consulta as
  // medições; o bloqueio era o front, que só montava o payload a partir do fit.
  // `n_pontos` fica NULO de propósito: é o que distingue as duas procedências.
  async function salvarCurvaManual(v: any) {
    setSalvandoCurva(true);
    try {
      await api('op_curva_salvar', {
        estacao_id: estacaoId,
        a: v.a, n: v.n, h0: v.h0,
        r2: v.r2 ?? null,
        cota_min: v.cota_min ?? null, cota_max: v.cota_max ?? null,
        n_pontos: null,
        vigencia_inicio: v.vigencia_inicio ? v.vigencia_inicio.format('YYYY-MM-DD') : null,
        observacao: v.observacao || null,
      });
      message.success('Curva-chave informada. A anterior foi arquivada.');
      setCurvaManualOpen(false);
      carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvandoCurva(false); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: largo ? 'row' : 'column', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 56%', minWidth: 0, width: largo ? undefined : '100%' }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
        Curva-chave — cada ponto é uma medição (cota × vazão); a linha vermelha é o ajuste por potência Q = a·(h − h₀)ⁿ.
      </div>
      <div style={{ height: largo ? 'clamp(320px, calc(100vh - 420px), 900px)' : 300, marginBottom: 10 }}>
        {/* A curva informada por fora existe SEM medição nenhuma — se o gate
            olhasse só `pontos`, ela nunca seria desenhada (era o caso que a
            faixa cota_min/cota_max do curvaPts foi feita pra atender). */}
        {pontos.length === 0 && curvaPts.length === 0 ? <Empty description="Sem medições e sem curva informada" style={{ paddingTop: 50 }} /> : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart margin={{ top: 8, right: 16, bottom: 16, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" dataKey="vazao" name="Vazão" tick={{ fontSize: 10 }} domain={['auto', 'auto']}
                label={{ value: 'Vazão (m³/s)', position: 'insideBottom', offset: -8, fontSize: 11 }} />
              <YAxis type="number" dataKey="cota" name="Cota" tick={{ fontSize: 10 }} domain={['auto', 'auto']}
                label={{ value: `Cota (${unidade})`, angle: -90, position: 'insideLeft', fontSize: 11 }} />
              <RTooltip formatter={(v: any, n: any) => [Number(v).toFixed(3), n === 'vazao' ? 'Vazão (m³/s)' : `Cota (${unidade})`]} />
              {curvaPts.length > 0 && (
                <Line data={curvaPts} dataKey="cota" stroke="#f5222d" strokeWidth={2} dot={false} isAnimationActive={false} />
              )}
              <Scatter data={pontos} fill="#0930D0" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {(ajuste || curvaSalva) && curvaAtiva ? (
        <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 10, marginBottom: 12 }}>
          <Row gutter={12} align="middle" wrap>
            <Col flex="auto">
              {curvaSalva
                ? (curvaAtiva.n_pontos == null
                    ? <Tag color="blue">Curva informada</Tag>
                    : <Tag color="green">Curva salva</Tag>)
                : <Tag color="orange">Ajuste (não salvo)</Tag>}
              <Text style={{ fontSize: 13 }}>
                Q = {nf(curvaAtiva.a, 4)}·(h − {nf(curvaAtiva.h0, 3)})<sup>{nf(curvaAtiva.n, 3)}</sup>
              </Text>
              {curvaAtiva.r2 != null && (
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 10 }}>R² = {nf(curvaAtiva.r2, 4)}</Text>
              )}
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                {curvaSalva
                  ? (curvaAtiva.n_pontos == null
                      ? 'ajustada fora do sistema'
                      : `ajustada pelo sistema · ${curvaAtiva.n_pontos} medições`)
                  : `ajuste automático sobre ${pontos.length} medições — ainda não salvo`}
                {curvaAtiva.cota_min != null && curvaAtiva.cota_max != null
                  && ` · válida de ${nf(curvaAtiva.cota_min, 2)} a ${nf(curvaAtiva.cota_max, 2)} ${unidade}`}
                {curvaAtiva.vigencia_inicio && ` · desde ${String(curvaAtiva.vigencia_inicio).slice(0, 10).split('-').reverse().join('/')}`}
              </div>
              {curvaAtiva.observacao && (
                <div style={{ fontSize: 11, color: '#777', marginTop: 2, fontStyle: 'italic' }}>{curvaAtiva.observacao}</div>
              )}
            </Col>
            {podeGerenciar && (
              <Col>
                <Space size={6}>
                  {ajuste && (
                    <Button size="small" type="primary" loading={salvandoCurva} onClick={salvarCurva}>
                      {curvaSalva ? 'Reajustar e salvar' : 'Salvar curva'}
                    </Button>
                  )}
                  <Button size="small" onClick={() => setCurvaManualOpen(true)}>Informar curva…</Button>
                </Space>
              </Col>
            )}
          </Row>
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Converter cota → vazão:</Text>
            <InputNumber decimalSeparator="," size="small" step={0.01} placeholder={`cota (${unidade})`} value={cotaConv}
              onChange={(v) => setCotaConv(v as number)} style={{ width: 130 }} />
            <Text style={{ fontSize: 13 }}>
              {vazaoConv != null ? <b>Q ≈ {nf(vazaoConv, 3)} m³/s</b>
                : (cotaConv != null ? <span style={{ color: '#faad14' }}>fora da curva (cota ≤ h₀)</span> : '—')}
            </Text>
          </div>
        </div>
      ) : podeGerenciar ? (
        // Sem curva E sem ajuste possível (<3 medições): é exatamente aqui que
        // entra a curva feita por fora — antes não havia porta nenhuma.
        <div style={{ background: '#fafafa', border: '1px dashed #d9d9d9', borderRadius: 8, padding: 10, marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sem curva-chave. Com 3 ou mais medições de cota × vazão o sistema ajusta sozinha;
            se você já tem uma curva pronta (planilha, estudo de terceiro), informe os parâmetros.
          </Text>
          <div style={{ marginTop: 8 }}>
            <Button size="small" type="primary" onClick={() => setCurvaManualOpen(true)}>Informar curva…</Button>
          </div>
        </div>
      ) : null}
      {curvaManualOpen && (
        <ModalCurvaManual unidade={unidade} salvando={salvandoCurva}
          inicial={curvaSalva}
          onFechar={() => setCurvaManualOpen(false)}
          onSalvar={salvarCurvaManual} />
      )}
      </div>

      <Card size="small" title="Medições de vazão" style={{ flex: '1 1 44%', minWidth: 0, width: largo ? undefined : '100%' }}
        extra={<Button size="small" icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>Importar cota×vazão</Button>}>
      <Space wrap align="end" style={{ marginBottom: 10 }}>
        <div><Text type="secondary" style={{ fontSize: 12 }}>Data</Text><br /><DatePicker showTime value={nova.data} format="DD/MM/YYYY HH:mm" onChange={(v) => setNova(s => ({ ...s, data: v }))} /></div>
        <div><Text type="secondary" style={{ fontSize: 12 }}>Cota ({unidade})</Text><br /><InputNumber decimalSeparator="," value={nova.cota} step={0.01} style={{ width: 110 }} onChange={(v) => setNova(s => ({ ...s, cota: v as number }))} /></div>
        <div><Text type="secondary" style={{ fontSize: 12 }}>Vazão (m³/s)</Text><br /><InputNumber decimalSeparator="," value={nova.vazao} step={0.01} style={{ width: 110 }} onChange={(v) => setNova(s => ({ ...s, vazao: v as number }))} /></div>
        <Button type="primary" loading={salvando} onClick={adicionar}>Adicionar medição</Button>
      </Space>
      <Table size="small" rowKey="id" loading={loading} dataSource={medicoes}
        pagination={largo ? { pageSize: 50, hideOnSinglePage: true, size: 'small' } : { pageSize: 8 }}
        scroll={largo ? { y: 'calc(100vh - 420px)' } : undefined}
        columns={[
          { title: 'Data', dataIndex: 'data_hora', render: (v: string) => dayjs(v).format('DD/MM/YY HH:mm') },
          { title: `Cota (${unidade})`, dataIndex: 'cota', render: (v: any) => v != null ? nf(v) : '—' },
          { title: 'Vazão (m³/s)', dataIndex: 'vazao', render: (v: any) => nf(v, 3) },
          { title: 'Método', dataIndex: 'metodo', render: (v: string) => v || '—' },
          { title: '', key: 'x', width: 100, render: (_: any, m: any) => (
            <Space size={2}>
              <Tooltip title="Detalhe / memória de cálculo">
                <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => setDetalhe(m)} />
              </Tooltip>
              {podeEditar && (
                <Tooltip title="Corrigir data">
                  <Button size="small" type="text" icon={<EditOutlined />} onClick={() => abrirEdicao(m)} />
                </Tooltip>
              )}
              <Popconfirm title="Excluir medição?" okText="Excluir" cancelText="Cancelar" okButtonProps={{ danger: true }} onConfirm={() => excluir(m.id)}>
                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          ) },
        ] as any} />
      </Card>
      {importOpen && <ModalImportarVazao estacaoId={estacaoId} unidade={unidade} onClose={() => setImportOpen(false)} onImportado={() => { setImportOpen(false); carregar(); }} />}
      {editando && (
        <Modal open onCancel={() => setEditando(null)} title="Corrigir data da medição"
          okText="Salvar" confirmLoading={salvandoEdit} onOk={salvarEdicao}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Q = {nf(editando.vazao, 3)} m³/s{editando.cota != null ? ` · cota ${nf(editando.cota)}` : ''}. Ajuste a data/hora da campanha:
          </Text>
          <div style={{ marginTop: 12 }}>
            <DatePicker showTime value={editData} format="DD/MM/YYYY HH:mm" style={{ width: '100%' }}
              onChange={(v) => setEditData(v)} />
          </div>
        </Modal>
      )}
      {detalhe && <ModalDetalheMedicao medicao={detalhe} unidade={unidade} estacaoCodigo={codigo} estacaoNome={nome} onClose={() => setDetalhe(null)} />}
    </div>
  );
}

// Modal de detalhamento de uma medição de vazão — memória de cálculo (verticais,
// pontos, área e Q por segmento) pra consistência e revisão. Item 1.
function ModalDetalheMedicao({ medicao, unidade, estacaoCodigo, estacaoNome, onClose }: { medicao: any; unidade: string; estacaoCodigo?: string; estacaoNome?: string; onClose: () => void }) {
  const vj = medicao?.verticais;
  const verts: any[] = Array.isArray(vj) ? vj : (vj?.verticais || []);
  const molinete = vj?.molinete;
  const POS: Record<string, string> = { superficie: 'Superfície', '0.2': '0,2', '0.4': '0,4', '0.6': '0,6', '0.8': '0,8', fundo: 'Fundo' };
  const hh = (d: any) => (d ? dayjs(d).format('HH:mm') : null);
  const periodo = [hh(medicao.data_hora), hh(medicao.data_hora_fim)].filter(Boolean).join(' – ') || '—';
  const equipeArr: string[] = Array.isArray(medicao.equipe) ? medicao.equipe : [];
  const cotaTxt = (v: any) => (v != null ? `${nf(v, 2)} ${unidade}` : '—');
  const molineteTxt = molinete ? [molinete.codigo, molinete.marca, molinete.modelo].filter(Boolean).join(' ') : (medicao.equipamento || '—');
  const coords = (medicao.latitude != null && medicao.longitude != null)
    ? `${nf(medicao.latitude, 5)}, ${nf(medicao.longitude, 5)}` : null;
  // Memória de cálculo (meia-seção): largura de influência, área e Q por vertical.
  const linhas = verts.map((v: any, i: number) => {
    const d = Number(v.profundidade) || 0; const vm = Number(v.v_media) || 0;
    const xPrev = i === 0 ? Number(verts[0].distancia) : Number(verts[i - 1].distancia);
    const xNext = i === verts.length - 1 ? Number(verts[verts.length - 1].distancia) : Number(verts[i + 1].distancia);
    const b = (xNext - xPrev) / 2; const area = b * d;
    return { ordem: v.ordem ?? i + 1, x: Number(v.distancia), d, vm, b, area, q: area * vm, pontos: v.pontos || [] };
  });
  return (
    <Modal open onCancel={onClose} width={820} title="Medição de descarga líquida — memória de cálculo"
      footer={<Button onClick={onClose}>Fechar</Button>}>
      {/* Cabeçalho estilo ficha de campo */}
      <Descriptions
        bordered size="small" column={2} style={{ marginBottom: 12 }}
        labelStyle={{ width: 120, fontSize: 12, background: '#fafafa' }}
        contentStyle={{ fontSize: 12 }}
        title={estacaoCodigo ? <span><EnvironmentOutlined /> {estacaoCodigo}{estacaoNome ? ` — ${estacaoNome}` : ''}</span> : undefined}
      >
        <Descriptions.Item label="Data">{medicao.data_hora ? dayjs(medicao.data_hora).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
        <Descriptions.Item label="Horário">{periodo}</Descriptions.Item>
        <Descriptions.Item label="Executor">{medicao.executor || '—'}</Descriptions.Item>
        <Descriptions.Item label="Equipe">{equipeArr.length ? equipeArr.map((e) => e.split('@')[0]).join(', ') : '—'}</Descriptions.Item>
        <Descriptions.Item label="Molinete">{molineteTxt}</Descriptions.Item>
        <Descriptions.Item label="Hélice">{molinete?.helice || '—'}</Descriptions.Item>
        <Descriptions.Item label="Cota início">{cotaTxt(medicao.cota_inicio)}</Descriptions.Item>
        <Descriptions.Item label="Cota fim">{cotaTxt(medicao.cota_fim)}</Descriptions.Item>
        <Descriptions.Item label="Cota média"><strong>{cotaTxt(medicao.cota)}</strong></Descriptions.Item>
        <Descriptions.Item label="Método">Área × velocidade (meia-seção)</Descriptions.Item>
        <Descriptions.Item label="Coordenadas" span={2}>
          {coords ? (
            <a href={`https://www.google.com/maps?q=${medicao.latitude},${medicao.longitude}`} target="_blank" rel="noreferrer">{coords}</a>
          ) : '—'}
        </Descriptions.Item>
      </Descriptions>

      <Row gutter={8} style={{ marginBottom: 8 }}>
        <Col span={6}><Statistic title="Vazão" value={nf(medicao.vazao, 3)} suffix="m³/s" valueStyle={{ fontSize: 16, color: '#1677ff' }} /></Col>
        <Col span={6}><Statistic title="Área molhada" value={nf(medicao.area_molhada, 3)} suffix="m²" valueStyle={{ fontSize: 16 }} /></Col>
        <Col span={6}><Statistic title="v média" value={nf(medicao.velocidade_media, 3)} suffix="m/s" valueStyle={{ fontSize: 16 }} /></Col>
        <Col span={6}><Statistic title="Largura · Prof. méd" value={`${nf(medicao.largura, 2)} · ${nf(medicao.profundidade_media, 2)} m`} valueStyle={{ fontSize: 14 }} /></Col>
      </Row>
      {verts.length === 0 ? (
        <Empty description="Sem detalhe das verticais (medição antiga ou sem pontos registrados)." />
      ) : (
        <Table size="small" rowKey="ordem" pagination={false} dataSource={linhas}
          expandable={{
            expandedRowRender: (r: any) => (
              <Table size="small" rowKey={(_: any, i?: number) => String(i)} pagination={false} dataSource={r.pontos}
                columns={[
                  { title: 'Posição', dataIndex: 'posicao', render: (p: string) => POS[p] || p },
                  { title: 'Rotações', dataIndex: 'rotacoes' },
                  { title: 'Tempo (s)', dataIndex: 'tempo_s' },
                  { title: 'v (m/s)', dataIndex: 'v', render: (v: any) => v != null ? nf(v, 3) : '—' },
                ] as any} />
            ),
          }}
          columns={[
            { title: 'Vert.', dataIndex: 'ordem', width: 48 },
            { title: 'Dist. (m)', dataIndex: 'x', render: (v: any) => nf(v, 2) },
            { title: 'Prof. (m)', dataIndex: 'd', render: (v: any) => nf(v, 2) },
            { title: 'v méd (m/s)', dataIndex: 'vm', render: (v: any) => nf(v, 3) },
            { title: 'Larg. infl. (m)', dataIndex: 'b', render: (v: any) => nf(v, 2) },
            { title: 'Área (m²)', dataIndex: 'area', render: (v: any) => nf(v, 3) },
            { title: 'Q parcial (m³/s)', dataIndex: 'q', render: (v: any) => nf(v, 4) },
          ] as any} />
      )}
    </Modal>
  );
}

function ModalImportarVazao({ estacaoId, unidade, onClose, onImportado }: {
  estacaoId: string; unidade: string; onClose: () => void; onImportado: () => void;
}) {
  const { message } = AntApp.useApp();
  const [linhas, setLinhas] = useState<Record<string, any>[]>([]);
  const [colunas, setColunas] = useState<string[]>([]);
  const [colData, setColData] = useState<string | undefined>();
  const [colCota, setColCota] = useState<string | undefined>();
  const [colVazao, setColVazao] = useState<string | undefined>();
  const [fileName, setFileName] = useState('');
  const [importando, setImportando] = useState(false);

  async function lerArquivo(file: File) {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json: Record<string, any>[] = XLSX.utils.sheet_to_json(ws, { defval: null });
      if (!json.length) { message.warning('Planilha vazia'); return; }
      const cols = Object.keys(json[0]);
      setLinhas(json); setColunas(cols); setFileName(file.name);
      setColData(cols.find(c => /data|hora|date/i.test(c)) || cols.find(c => parseLidoEmCelula(json[0][c]) != null));
      setColCota(cols.find(c => /cota|n[íi]vel|stage/i.test(c)));
      setColVazao(cols.find(c => /vaz|flow|discharge/i.test(c)) || cols.find(c => parseValorCelula(json[0][c]) != null));
    } catch (e: any) { message.error('Erro ao ler: ' + e.message); }
  }
  const previa = useMemo(() => {
    if (!colData || !colVazao) return { validas: [] as any[], total: linhas.length };
    const validas = linhas.map(l => ({
      data_hora: parseLidoEmCelula(l[colData]),
      cota: colCota ? parseValorCelula(l[colCota]) : null,
      vazao: parseValorCelula(l[colVazao]),
    })).filter(x => x.data_hora && x.vazao !== null);
    return { validas, total: linhas.length };
  }, [linhas, colData, colCota, colVazao]);

  async function importar() {
    if (!previa.validas.length) { message.warning('Nenhuma linha válida.'); return; }
    setImportando(true);
    try {
      const r = await api('op_vazao_importar', { estacao_id: estacaoId, medicoes: previa.validas });
      message.success(`${r.importadas} importada(s)${r.ignoradas ? `, ${r.ignoradas} ignorada(s)` : ''}.`);
      onImportado();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setImportando(false); }
  }
  const opts = colunas.map(c => ({ value: c, label: c }));
  return (
    <Modal open title="Importar cota × vazão (Excel / CSV)" onCancel={onClose} width={560}
      okText={`Importar ${previa.validas.length || ''}`.trim()} okButtonProps={{ disabled: !previa.validas.length }}
      confirmLoading={importando} onOk={importar}>
      <input type="file" accept=".xlsx,.xls,.csv" style={{ marginBottom: 12 }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) lerArquivo(f); }} />
      {linhas.length > 0 && (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>{fileName} — {linhas.length} linha(s). Diga qual coluna é o quê:</Text>
          <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={8}><Text style={{ fontSize: 12 }}>Data/hora *</Text><Select size="small" style={{ width: '100%' }} value={colData} onChange={setColData} options={opts} /></Col>
            <Col span={8}><Text style={{ fontSize: 12 }}>Cota ({unidade})</Text><Select size="small" allowClear style={{ width: '100%' }} value={colCota} onChange={setColCota} options={opts} /></Col>
            <Col span={8}><Text style={{ fontSize: 12 }}>Vazão (m³/s) *</Text><Select size="small" style={{ width: '100%' }} value={colVazao} onChange={setColVazao} options={opts} /></Col>
          </Row>
          <div style={{ marginTop: 12, padding: 8, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
            <Text>{previa.validas.length}</Text> de <Text>{previa.total}</Text> válida(s)
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Molinetes (V3.1): cadastro do equipamento de medição de velocidade ─────────
